// Définition des labs (CKAD / CKA / CKS).
//
// Chaque lab est une suite de tâches (« questions » de l'examen). Une tâche a un
// validateur PUR qui inspecte le snapshot du cluster (voir ClusterEngine.snapshot())
// et renvoie true dès que l'état attendu est atteint. La validation est
// ré-évaluée après chaque commande — le cœur du format « performance-based ».
//
// RÈGLE D'OR : une tâche ne doit utiliser QUE des commandes réellement
// supportées par le moteur (voir engine.ts / helpText). Chaque tâche a été
// testée : la commande de la solution produit bien l'état attendu.
//
// Les labs sont regroupés par slug de certification (voir handsOn.ts).

import type { ClusterState } from './engine';
import { LAB_SCENARIOS } from './labScenarios';
import { LAB_INSTRUCTIONS } from './labInstructions';

export interface LabTask {
  id: string;
  instruction: string;
  hint?: string;
  validate: (state: ClusterState) => boolean;
}

export interface Lab {
  id: string;
  cert: string;
  title: string;
  level: 'CKAD' | 'CKA' | 'CKS';
  summary: string;
  /**
   * Problématique de 2-3 paragraphes (contexte/incident réaliste type examen)
   * affichée au-dessus des tâches. Injectée depuis labScenarios.ts.
   */
  scenario?: string;
  /** Fichiers pré-remplis dans le terminal (ex. manifestes à appliquer). */
  files?: { path: string; content: string }[];
  /**
   * Manifestes YAML appliqués à l'ÉTAT du cluster au chargement du lab (avant
   * toute commande), pour les scénarios supposant des ressources déjà en place
   * (ex. un StatefulSet à réduire, des pods existants à isoler). Voir
   * ClusterEngine.seed().
   */
  seed?: string[];
  tasks: LabTask[];
}

const CKAD = 'kubernetes-certified-application-developer-ckad';
const CKA = 'kubernetes-certified-administrator-cka';
const CKS = 'kubernetes-security-specialist-cks';

// Helpers de validation ------------------------------------------------------
const dep = (s: ClusterState, name: string, ns = 'default') => s.deployments.find((d) => d.name === name && d.namespace === ns);
const role = (s: ClusterState, name: string, ns = 'default') => s.roles.find((r) => r.name === name && r.namespace === ns);
const pod = (s: ClusterState, name: string, ns = 'default') => s.pods.find((p) => p.name === name && p.namespace === ns);
const pvc = (s: ClusterState, name: string, ns = 'default') => s.persistentvolumeclaims.find((c) => c.name === name && c.namespace === ns);
const sc = (s: ClusterState, name: string) => s.storageclasses.find((x) => x.name === name);
const sts = (s: ClusterState, name: string, ns = 'default') => s.statefulsets.find((x) => x.name === name && x.namespace === ns);
const ds = (s: ClusterState, name: string, ns = 'default') => s.daemonsets.find((x) => x.name === name && x.namespace === ns);
const hpa = (s: ClusterState, name: string, ns = 'default') => s.horizontalpodautoscalers.find((x) => x.name === name && x.namespace === ns);
const netpol = (s: ClusterState, name: string, ns = 'default') => s.networkpolicies.find((x) => x.name === name && x.namespace === ns);
const pv = (s: ClusterState, name: string) => s.persistentvolumes.find((x) => x.name === name);
const hasAll = (arr: string[], need: string[]) => need.every((v) => arr.includes(v));

export const LABS: Lab[] = [
  // ════════════════════════════════════════════════════════════════ CKAD
  {
    id: 'ckad-deploy-expose',
    cert: CKAD,
    title: 'Déploiement, montée en charge et exposition',
    level: 'CKAD',
    summary: 'Le pain quotidien du CKAD : créer un Deployment, le scaler, l’exposer via un Service.',
    tasks: [
      {
        id: 'create-deploy',
        instruction: 'Dans le namespace « default », crée un Deployment nommé « web » avec l’image « nginx:1.25 » et 3 replicas.',
        hint: 'kubectl create deployment web --image=nginx:1.25 --replicas=3',
        validate: (s) => { const d = dep(s, 'web'); return !!d && d.image === 'nginx:1.25' && d.replicas === 3; },
      },
      {
        id: 'pods-running',
        instruction: 'Vérifie que les 3 pods du Deployment « web » sont en état Running (kubectl get pods).',
        hint: 'kubectl get pods',
        validate: (s) => s.pods.filter((p) => p.ownerName === 'web' && p.status === 'Running').length === 3,
      },
      {
        id: 'expose',
        instruction: 'Expose le Deployment « web » avec un Service ClusterIP sur le port 80 (target-port 80).',
        hint: 'kubectl expose deployment web --port=80 --target-port=80',
        validate: (s) => s.services.some((svc) => svc.selector.app === 'web' && svc.port === 80),
      },
      {
        id: 'scale-up',
        instruction: 'Le trafic augmente : monte « web » à 5 replicas.',
        hint: 'kubectl scale deployment web --replicas=5',
        validate: (s) => { const d = dep(s, 'web'); return !!d && d.replicas === 5; },
      },
    ],
  },
  {
    id: 'ckad-namespaces-config',
    cert: CKAD,
    title: 'Namespaces, ConfigMaps et Secrets',
    level: 'CKAD',
    summary: 'Isole une application dans son namespace et provisionne sa configuration et ses secrets.',
    tasks: [
      {
        id: 'create-ns',
        instruction: 'Crée un namespace nommé « ecommerce ».',
        hint: 'kubectl create namespace ecommerce',
        validate: (s) => s.namespaces.some((n) => n.name === 'ecommerce'),
      },
      {
        id: 'deploy-in-ns',
        instruction: 'Déploie « api » (image « httpd:2.4 ») dans le namespace « ecommerce ».',
        hint: 'kubectl create deployment api --image=httpd:2.4 -n ecommerce',
        validate: (s) => !!dep(s, 'api', 'ecommerce') && dep(s, 'api', 'ecommerce')!.image === 'httpd:2.4',
      },
      {
        id: 'configmap',
        instruction: 'Crée un ConfigMap « api-config » avec la donnée LOG_LEVEL=debug dans « ecommerce ».',
        hint: 'kubectl create configmap api-config --from-literal=LOG_LEVEL=debug -n ecommerce',
        validate: (s) => s.configmaps.some((c) => c.name === 'api-config' && c.namespace === 'ecommerce' && c.data.LOG_LEVEL === 'debug'),
      },
      {
        id: 'secret',
        instruction: 'Crée un Secret générique « api-db » avec DB_PASSWORD=Sup3rS3cret dans « ecommerce ».',
        hint: 'kubectl create secret generic api-db --from-literal=DB_PASSWORD=Sup3rS3cret -n ecommerce',
        validate: (s) => s.secrets.some((sec) => sec.name === 'api-db' && sec.namespace === 'ecommerce' && sec.data.DB_PASSWORD === 'Sup3rS3cret'),
      },
    ],
  },
  {
    id: 'ckad-rollout',
    cert: CKAD,
    title: 'Mise à jour continue et rollback',
    level: 'CKAD',
    summary: 'Déploie une version, mets à jour l’image, puis reviens en arrière après un incident.',
    tasks: [
      {
        id: 'create',
        instruction: 'Crée un Deployment « payments » avec l’image « myshop/payments:1.0 ».',
        hint: 'kubectl create deployment payments --image=myshop/payments:1.0',
        validate: (s) => { const d = dep(s, 'payments'); return !!d && d.image === 'myshop/payments:1.0'; },
      },
      {
        id: 'update',
        instruction: 'Déploie la version 2.0 : mets à jour l’image du conteneur « payments » vers « myshop/payments:2.0 ».',
        hint: 'kubectl set image deployment/payments payments=myshop/payments:2.0',
        validate: (s) => { const d = dep(s, 'payments'); return !!d && d.history.some((h) => h.image === 'myshop/payments:2.0'); },
      },
      {
        id: 'rollback',
        instruction: 'La 2.0 est buguée : effectue un rollback (l’image doit repasser à myshop/payments:1.0).',
        hint: 'kubectl rollout undo deployment/payments',
        validate: (s) => { const d = dep(s, 'payments'); return !!d && d.image === 'myshop/payments:1.0' && d.revision >= 3; },
      },
    ],
  },
  {
    id: 'ckad-declarative',
    cert: CKAD,
    title: 'Approche déclarative (generate & apply)',
    level: 'CKAD',
    summary: 'Le flux CKAD par excellence : générer un manifeste avec --dry-run=client -o yaml, puis l’appliquer.',
    tasks: [
      {
        id: 'gen-apply-deploy',
        instruction: 'Génère le manifeste d’un Deployment « catalog » (image « nginx », 2 replicas) avec --dry-run=client -o yaml dans catalog.yaml, puis applique-le.',
        hint: 'kubectl create deployment catalog --image=nginx --replicas=2 --dry-run=client -o yaml > catalog.yaml\nkubectl apply -f catalog.yaml',
        validate: (s) => { const d = dep(s, 'catalog'); return !!d && d.replicas === 2 && d.image.startsWith('nginx'); },
      },
      {
        id: 'gen-apply-pod',
        instruction: 'Génère puis applique un Pod « cache » (image « redis:7 ») via un manifeste cache.yaml.',
        hint: 'kubectl run cache --image=redis:7 --dry-run=client -o yaml > cache.yaml\nkubectl apply -f cache.yaml',
        validate: (s) => s.pods.some((p) => p.name === 'cache' && p.containers.some((c) => c.image === 'redis:7')),
      },
    ],
  },
  {
    id: 'ckad-pod-run',
    cert: CKAD,
    title: 'Pods impératifs et diagnostic',
    level: 'CKAD',
    summary: 'Lancer des pods rapidement (kubectl run) et inspecter le cluster.',
    tasks: [
      {
        id: 'run-pod',
        instruction: 'Lance un Pod nommé « busybox » avec l’image « busybox:1.36 » dans le namespace « default ».',
        hint: 'kubectl run busybox --image=busybox:1.36',
        validate: (s) => s.pods.some((p) => p.name === 'busybox' && p.namespace === 'default' && p.containers[0]?.image === 'busybox:1.36'),
      },
      {
        id: 'run-labeled',
        instruction: 'Lance un Pod « nginx-prod » (image « nginx ») puis ajoute-lui le label env=prod.',
        hint: 'kubectl run nginx-prod --image=nginx\nkubectl label pod nginx-prod env=prod',
        validate: (s) => s.pods.some((p) => p.name === 'nginx-prod' && p.labels.env === 'prod'),
      },
    ],
  },

  {
    id: 'ckad-storage-probes',
    cert: CKAD,
    title: 'Stockage persistant, volumes et sondes',
    level: 'CKAD',
    summary: 'StorageClass, PVC lié, volume monté dans un pod, sondes de santé et commande de conteneur. Les manifestes sont fournis dans le terminal — applique-les avec kubectl apply -f.',
    files: [
      {
        path: 'storageclass.yaml',
        content: [
          'apiVersion: storage.k8s.io/v1',
          'kind: StorageClass',
          'metadata:',
          '  name: fast',
          'provisioner: kubernetes.io/no-provisioner',
          'reclaimPolicy: Retain',
          'volumeBindingMode: WaitForFirstConsumer',
        ].join('\n'),
      },
      {
        path: 'pvc.yaml',
        content: [
          'apiVersion: v1',
          'kind: PersistentVolumeClaim',
          'metadata:',
          '  name: data',
          '  namespace: default',
          'spec:',
          '  accessModes:',
          '  - ReadWriteOnce',
          '  storageClassName: standard',
          '  resources:',
          '    requests:',
          '      storage: 1Gi',
        ].join('\n'),
      },
      {
        path: 'pod-volume.yaml',
        content: [
          'apiVersion: v1',
          'kind: Pod',
          'metadata:',
          '  name: app',
          'spec:',
          '  volumes:',
          '  - name: web',
          '    persistentVolumeClaim:',
          '      claimName: data',
          '  containers:',
          '  - name: app',
          '    image: nginx',
          '    volumeMounts:',
          '    - name: web',
          '      mountPath: /usr/share/nginx/html',
        ].join('\n'),
      },
      {
        path: 'pod-probe.yaml',
        content: [
          'apiVersion: v1',
          'kind: Pod',
          'metadata:',
          '  name: healthy',
          'spec:',
          '  containers:',
          '  - name: healthy',
          '    image: nginx',
          '    livenessProbe:',
          '      httpGet:',
          '        path: /',
          '        port: 80',
          '      initialDelaySeconds: 5',
          '      periodSeconds: 10',
        ].join('\n'),
      },
      {
        path: 'pod-command.yaml',
        content: [
          'apiVersion: v1',
          'kind: Pod',
          'metadata:',
          '  name: ticker',
          'spec:',
          '  containers:',
          '  - name: ticker',
          '    image: busybox:1.36',
          '    command: ["sh", "-c", "while true; do date; sleep 5; done"]',
        ].join('\n'),
      },
    ],
    tasks: [
      {
        id: 'create-storageclass',
        instruction:
          'Applique storageclass.yaml pour créer la StorageClass « fast » (provisioner kubernetes.io/no-provisioner, volumeBindingMode WaitForFirstConsumer).',
        hint: 'kubectl apply -f storageclass.yaml\n# vérifie : kubectl get sc',
        validate: (s) => {
          const x = sc(s, 'fast');
          return !!x && x.provisioner === 'kubernetes.io/no-provisioner' && x.volumeBindingMode === 'WaitForFirstConsumer';
        },
      },
      {
        id: 'create-pvc',
        instruction:
          'Applique pvc.yaml : un PersistentVolumeClaim « data » demandant 1Gi en ReadWriteOnce via la StorageClass « standard ».',
        hint: 'kubectl apply -f pvc.yaml\n# vérifie : kubectl get pvc',
        validate: (s) => {
          const c = pvc(s, 'data');
          return !!c && c.storageRequest === '1Gi' && c.accessModes.includes('ReadWriteOnce') && c.storageClassName === 'standard';
        },
      },
      {
        id: 'pvc-bound',
        instruction:
          'Vérifie que le PVC « data » est lié (Bound) — la StorageClass « standard » (Immediate) le provisionne dynamiquement.',
        hint: 'kubectl get pvc data\n# le PVC passe Bound tout seul avec la StorageClass standard',
        validate: (s) => pvc(s, 'data')?.status === 'Bound',
      },
      {
        id: 'pod-volume-mount',
        instruction:
          'Applique pod-volume.yaml : un Pod « app » (nginx) qui monte le PVC « data » sur /usr/share/nginx/html via le volume « web ».',
        hint: 'kubectl apply -f pod-volume.yaml\n# vérifie : kubectl describe pod app',
        validate: (s) => {
          const p = pod(s, 'app');
          if (!p) return false;
          const vol = (p.volumes || []).find((v) => v.name === 'web' && v.kind === 'persistentVolumeClaim' && v.source === 'data');
          const mount = p.containers[0]?.volumeMounts?.some((m) => m.name === 'web' && m.mountPath === '/usr/share/nginx/html');
          return !!vol && !!mount;
        },
      },
      {
        id: 'pod-probe',
        instruction:
          'Applique pod-probe.yaml : un Pod « healthy » (nginx) avec une livenessProbe httpGet sur / port 80.',
        hint: 'kubectl apply -f pod-probe.yaml\n# vérifie : kubectl describe pod healthy',
        validate: (s) => {
          const probe = pod(s, 'healthy')?.containers[0]?.livenessProbe;
          return !!probe && probe.kind === 'httpGet' && probe.path === '/' && String(probe.port) === '80';
        },
      },
      {
        id: 'pod-command',
        instruction:
          'Applique pod-command.yaml : un Pod « ticker » (busybox:1.36) dont la commande est sh -c "while true; do date; sleep 5; done".',
        hint: 'kubectl apply -f pod-command.yaml\n# vérifie : kubectl describe pod ticker',
        validate: (s) => {
          const cmd = pod(s, 'ticker')?.containers[0]?.command;
          return !!cmd && cmd[0] === 'sh' && cmd[1] === '-c' && cmd.join(' ').includes('while true');
        },
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════ CKA
  // ─────────────────────────────────────────────────────────────────────────
  // CKA Simulator A — grand examen de simulation type killer.sh : 17 questions
  // à résoudre dans le même terminal simulé. Chaque question est notée
  // indépendamment (validateur pur sur l'état du cluster). Seules les questions
  // reproductibles dans le moteur sont retenues (les questions purement
  // niveau-nœud — kubeconfig, Helm, etcd, kubeadm, crictl, certs, Gateway API —
  // ne sont pas simulables et sont omises).
  {
    id: 'cka-simulator-a',
    cert: CKA,
    title: 'CKA Simulator A — Kubernetes 1.35',
    level: 'CKA',
    summary: "Examen de simulation complet : 17 questions couvrant RBAC, workloads, StatefulSet, DaemonSet, HPA, stockage (PV/PVC), anti-affinité, NetworkPolicy et administration.",
    scenario:
      "CKA Simulator A — 17 questions, un seul environnement.\n\n" +
      "Cet examen reproduit le format killer.sh : vous enchaînez les questions dans le même terminal, chacune notée séparément. Naviguez de question en question, marquez celles à revoir, et surveillez le temps.\n\n" +
      "Sur le vrai examen chaque question se résout sur une instance dédiée (ssh ckaXXXX) ; ici tout se joue dans ce terminal kubectl simulé. Les ressources supposées « déjà en place » (StatefulSet à réduire, Deployments existants, pods à isoler) sont pré-provisionnées. Les manifestes fournis sont dans le répertoire courant (ls pour les lister).\n\n" +
      "Astuce : kubectl accepte le flag de namespace avant OU après le verbe (k -n project-h800 get pod).",
    seed: [
      // Q1 : StatefulSet o3db (2 replicas) déjà en place
      ['apiVersion: apps/v1', 'kind: StatefulSet', 'metadata:', '  name: o3db', '  namespace: project-h800',
       'spec:', '  serviceName: o3db', '  replicas: 2', '  selector:', '    matchLabels:', '      app: nginx',
       '  template:', '    metadata:', '      labels:', '        app: nginx', '    spec:', '      containers:',
       '      - name: nginx', '        image: nginx:1-alpine'].join('\n'),
      // Q4 : Deployments api-gateway (staging + prod) avec resources.requests
      ['apiVersion: apps/v1', 'kind: Deployment', 'metadata:', '  name: api-gateway', '  namespace: api-gateway-staging',
       'spec:', '  replicas: 1', '  selector:', '    matchLabels:', '      id: api-gateway', '  template:', '    metadata:',
       '      labels:', '        id: api-gateway', '    spec:', '      containers:', '      - name: httpd', '        image: httpd:2-alpine',
       '        resources:', '          requests:', '            cpu: 10m', '            memory: 20Mi'].join('\n'),
      ['apiVersion: apps/v1', 'kind: Deployment', 'metadata:', '  name: api-gateway', '  namespace: api-gateway-prod',
       'spec:', '  replicas: 1', '  selector:', '    matchLabels:', '      id: api-gateway', '  template:', '    metadata:',
       '      labels:', '        id: api-gateway', '    spec:', '      containers:', '      - name: httpd', '        image: httpd:2-alpine',
       '        resources:', '          requests:', '            cpu: 10m', '            memory: 20Mi'].join('\n'),
      // Q7 : pods de project-snake déjà en place
      ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: backend-0', '  namespace: project-snake', '  labels:', '    app: backend', 'spec:', '  containers:', '  - name: nginx', '    image: nginx:1-alpine'].join('\n'),
      ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: db1-0', '  namespace: project-snake', '  labels:', '    app: db1', 'spec:', '  containers:', '  - name: nginx', '    image: nginx:1-alpine'].join('\n'),
      ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: db2-0', '  namespace: project-snake', '  labels:', '    app: db2', 'spec:', '  containers:', '  - name: nginx', '    image: nginx:1-alpine'].join('\n'),
      ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: vault-0', '  namespace: project-snake', '  labels:', '    app: vault', 'spec:', '  containers:', '  - name: nginx', '    image: nginx:1-alpine'].join('\n'),
    ],
    files: [
      { path: '6_pv.yaml', content: ['apiVersion: v1', 'kind: PersistentVolume', 'metadata:', '  name: safari-pv', 'spec:', '  capacity:', '    storage: 2Gi', '  accessModes:', '  - ReadWriteOnce', '  hostPath:', '    path: /Volumes/Data'].join('\n') },
      { path: '6_pvc.yaml', content: ['apiVersion: v1', 'kind: PersistentVolumeClaim', 'metadata:', '  name: safari-pvc', '  namespace: project-t230', 'spec:', '  accessModes:', '  - ReadWriteOnce', '  resources:', '    requests:', '      storage: 2Gi'].join('\n') },
      { path: '6_dep.yaml', content: ['apiVersion: apps/v1', 'kind: Deployment', 'metadata:', '  name: safari', '  namespace: project-t230', 'spec:', '  replicas: 1', '  selector:', '    matchLabels:', '      app: safari', '  template:', '    metadata:', '      labels:', '        app: safari', '    spec:', '      volumes:', '      - name: data', '        persistentVolumeClaim:', '          claimName: safari-pvc', '      containers:', '      - name: container', '        image: httpd:2-alpine', '        volumeMounts:', '        - name: data', '          mountPath: /tmp/safari-data'].join('\n') },
      { path: '5_hpa_staging.yaml', content: ['apiVersion: autoscaling/v2', 'kind: HorizontalPodAutoscaler', 'metadata:', '  name: api-gateway', '  namespace: api-gateway-staging', 'spec:', '  scaleTargetRef:', '    apiVersion: apps/v1', '    kind: Deployment', '    name: api-gateway', '  minReplicas: 2', '  maxReplicas: 4', '  metrics:', '  - type: Resource', '    resource:', '      name: cpu', '      target:', '        type: Utilization', '        averageUtilization: 50'].join('\n') },
      { path: '5_hpa_prod.yaml', content: ['apiVersion: autoscaling/v2', 'kind: HorizontalPodAutoscaler', 'metadata:', '  name: api-gateway', '  namespace: api-gateway-prod', 'spec:', '  scaleTargetRef:', '    apiVersion: apps/v1', '    kind: Deployment', '    name: api-gateway', '  minReplicas: 2', '  maxReplicas: 6', '  metrics:', '  - type: Resource', '    resource:', '      name: cpu', '      target:', '        type: Utilization', '        averageUtilization: 50'].join('\n') },
      { path: '11_ds.yaml', content: ['apiVersion: apps/v1', 'kind: DaemonSet', 'metadata:', '  name: ds-important', '  namespace: project-tiger', '  labels:', '    id: ds-important', '    uuid: 18426a0b-5f59-4e10-923f-c0e078e82462', 'spec:', '  selector:', '    matchLabels:', '      id: ds-important', '  template:', '    metadata:', '      labels:', '        id: ds-important', '        uuid: 18426a0b-5f59-4e10-923f-c0e078e82462', '    spec:', '      containers:', '      - name: ds-important', '        image: httpd:2-alpine', '        resources:', '          requests:', '            cpu: 10m', '            memory: 10Mi', '      tolerations:', '      - effect: NoSchedule', '        key: node-role.kubernetes.io/control-plane'].join('\n') },
      { path: '12_dep.yaml', content: ['apiVersion: apps/v1', 'kind: Deployment', 'metadata:', '  name: deploy-important', '  namespace: project-tiger', '  labels:', '    id: very-important', 'spec:', '  replicas: 3', '  selector:', '    matchLabels:', '      id: very-important', '  template:', '    metadata:', '      labels:', '        id: very-important', '    spec:', '      containers:', '      - name: container1', '        image: nginx:1-alpine', '      - name: container2', '        image: registry.k8s.io/pause:3.10', '      affinity:', '        podAntiAffinity:', '          requiredDuringSchedulingIgnoredDuringExecution:', '          - labelSelector:', '              matchExpressions:', '              - key: id', '                operator: In', '                values:', '                - very-important', '            topologyKey: kubernetes.io/hostname'].join('\n') },
      { path: '15_np.yaml', content: ['apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: np-backend', '  namespace: project-snake', 'spec:', '  podSelector:', '    matchLabels:', '      app: backend', '  policyTypes:', '  - Egress', '  egress:', '  - to:', '    - podSelector:', '        matchLabels:', '          app: db1', '    ports:', '    - protocol: TCP', '      port: 1111', '  - to:', '    - podSelector:', '        matchLabels:', '          app: db2', '    ports:', '    - protocol: TCP', '      port: 2222'].join('\n') },
      { path: 'sidecar.yaml', content: ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: webapp', '  namespace: project-tiger', '  labels:', '    tier: frontend', 'spec:', '  containers:', '  - name: app', '    image: nginx:1.25', '  - name: log-agent', '    image: busybox:1.36', '    command: ["sh", "-c", "tail -f /dev/null"]'].join('\n') },
      { path: 'default-deny.yaml', content: ['apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: default-deny-ingress', '  namespace: secure', 'spec:', '  podSelector: {}', '  policyTypes:', '  - Ingress'].join('\n') },
      { path: 'allow-frontend.yaml', content: ['apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: allow-frontend', '  namespace: secure', 'spec:', '  podSelector:', '    matchLabels:', '      app: backend', '  policyTypes:', '  - Ingress', '  ingress:', '  - from:', '    - podSelector:', '        matchLabels:', '          app: frontend'].join('\n') },
      { path: 'storageclass.yaml', content: ['apiVersion: storage.k8s.io/v1', 'kind: StorageClass', 'metadata:', '  name: fast', 'provisioner: kubernetes.io/no-provisioner', 'reclaimPolicy: Retain', 'volumeBindingMode: WaitForFirstConsumer'].join('\n') },
      { path: 'data_pvc.yaml', content: ['apiVersion: v1', 'kind: PersistentVolumeClaim', 'metadata:', '  name: data', '  namespace: default', 'spec:', '  accessModes:', '  - ReadWriteOnce', '  storageClassName: standard', '  resources:', '    requests:', '      storage: 1Gi'].join('\n') },
    ],
    tasks: [
      {
        id: 'q1-statefulset',
        instruction:
          "**Question 1 | StatefulSet**\n\nDeux pods `o3db-*` tournent dans le namespace `project-h800`. Réduis-les à `1` replica pour économiser des ressources.\n\n- Identifie d'abord le contrôleur (`kubectl -n project-h800 get deploy,ds,sts`)\n- Réduis le StatefulSet `o3db` à `1`",
        hint: 'kubectl -n project-h800 get sts\nkubectl -n project-h800 scale sts o3db --replicas 1',
        validate: (s) => sts(s, 'o3db', 'project-h800')?.replicas === 1,
      },
      {
        id: 'q2-rbac-processor',
        instruction:
          "**Question 2 | RBAC — ServiceAccount, Role, RoleBinding**\n\nDans le namespace `project-hamster`, autorise un ServiceAccount à UNIQUEMENT créer des Secrets et ConfigMaps.\n\n- Crée le ServiceAccount `processor`\n- Crée un Role `processor` : verbe `create` sur `secrets` et `configmaps`\n- Crée un RoleBinding `processor` liant le Role au SA `project-hamster:processor`",
        hint: 'kubectl -n project-hamster create sa processor\nkubectl -n project-hamster create role processor --verb=create --resource=secret --resource=configmap\nkubectl -n project-hamster create rolebinding processor --role processor --serviceaccount project-hamster:processor',
        validate: (s) => {
          const sa = s.serviceaccounts.some((a) => a.name === 'processor' && a.namespace === 'project-hamster');
          const r = role(s, 'processor', 'project-hamster');
          const okRole = !!r && r.verbs.includes('create') && (r.resources.includes('secrets') || r.resources.includes('secret')) && (r.resources.includes('configmaps') || r.resources.includes('configmap'));
          const rb = s.rolebindings.some((b) => b.name === 'processor' && b.namespace === 'project-hamster' && b.roleName === 'processor' && b.subjects.some((u) => u.kind === 'ServiceAccount' && u.name === 'processor' && u.namespace === 'project-hamster'));
          return sa && okRole && rb;
        },
      },
      {
        id: 'q3-storage',
        instruction:
          "**Question 3 | Stockage — PV, PVC, volume**\n\nCrée le stockage pour `project-t230`. Les manifestes `6_pv.yaml`, `6_pvc.yaml`, `6_dep.yaml` sont fournis (l'ordre compte).\n\n- `safari-pv` : `2Gi`, `ReadWriteOnce`, hostPath `/Volumes/Data`, sans storageClassName\n- `safari-pvc` : `2Gi`, `ReadWriteOnce`, doit se lier (Bound) à `safari-pv`\n- Deployment `safari` (httpd:2-alpine) montant le PVC sur `/tmp/safari-data`",
        hint: 'kubectl apply -f 6_pv.yaml\nkubectl apply -f 6_pvc.yaml\nkubectl apply -f 6_dep.yaml',
        validate: (s) => {
          const v = pv(s, 'safari-pv');
          const okPv = !!v && v.capacity === '2Gi' && v.accessModes.includes('ReadWriteOnce') && v.hostPath === '/Volumes/Data' && v.storageClassName === null;
          const c = pvc(s, 'safari-pvc', 'project-t230');
          const okPvc = !!c && c.status === 'Bound' && c.volumeName === 'safari-pv';
          const d = dep(s, 'safari', 'project-t230');
          const okDep = !!d && (d.volumes || []).some((vol) => vol.kind === 'persistentVolumeClaim' && vol.source === 'safari-pvc') && !!d.templateContainers?.[0]?.volumeMounts?.some((m) => m.mountPath === '/tmp/safari-data');
          return okPv && okPvc && okDep;
        },
      },
      {
        id: 'q4-hpa',
        instruction:
          "**Question 4 | HorizontalPodAutoscaler**\n\nRemplace l'autoscaler externe de `api-gateway` par un HPA, en staging ET prod. Manifestes `5_hpa_staging.yaml`, `5_hpa_prod.yaml` fournis.\n\n- Staging (`api-gateway-staging`) : min `2`, max `4`, à `50%` CPU\n- Prod (`api-gateway-prod`) : min `2`, max `6`, à `50%` CPU",
        hint: 'kubectl apply -f 5_hpa_staging.yaml\nkubectl apply -f 5_hpa_prod.yaml\n# ou : kubectl -n api-gateway-staging autoscale deployment api-gateway --min=2 --max=4 --cpu-percent=50',
        validate: (s) => {
          const hs = hpa(s, 'api-gateway', 'api-gateway-staging');
          const hp = hpa(s, 'api-gateway', 'api-gateway-prod');
          return !!hs && hs.minReplicas === 2 && hs.maxReplicas === 4 && hs.cpuUtilization === 50 && !!hp && hp.minReplicas === 2 && hp.maxReplicas === 6 && hp.cpuUtilization === 50;
        },
      },
      {
        id: 'q5-daemonset',
        instruction:
          "**Question 5 | DaemonSet sur tous les nœuds**\n\nDans `project-tiger`, crée le DaemonSet `ds-important` (manifeste `11_ds.yaml`).\n\n- Image `httpd:2-alpine`, labels `id=ds-important` et `uuid=18426a0b-5f59-4e10-923f-c0e078e82462`\n- Requêtes `10m` CPU et `10Mi` mémoire\n- Tourne sur TOUS les nœuds (tolération control-plane)",
        hint: 'kubectl apply -f 11_ds.yaml\nkubectl -n project-tiger get ds',
        validate: (s) => {
          const d = ds(s, 'ds-important', 'project-tiger');
          if (!d) return false;
          const okReq = d.requests?.cpu === '10m' && d.requests?.memory === '10Mi';
          const okTol = (d.tolerations || []).some((t) => t.key === 'node-role.kubernetes.io/control-plane');
          const pods = s.pods.filter((p) => p.namespace === 'project-tiger' && p.ownerName === 'ds-important' && p.status === 'Running').length;
          return d.image === 'httpd:2-alpine' && d.labels.id === 'ds-important' && d.labels.uuid === '18426a0b-5f59-4e10-923f-c0e078e82462' && okReq && okTol && pods >= 3;
        },
      },
      {
        id: 'q6-antiaffinity',
        instruction:
          "**Question 6 | Deployment avec anti-affinité**\n\nDans `project-tiger`, crée `deploy-important` (manifeste `12_dep.yaml`) : 3 replicas, label `id=very-important`, deux conteneurs (`container1` nginx:1-alpine, `container2` registry.k8s.io/pause:3.10), avec une `podAntiAffinity` de topologyKey `kubernetes.io/hostname`.",
        hint: 'kubectl apply -f 12_dep.yaml',
        validate: (s) => {
          const d = dep(s, 'deploy-important', 'project-tiger');
          if (!d) return false;
          const c1 = d.templateContainers?.some((c) => c.name === 'container1' && c.image === 'nginx:1-alpine');
          const c2 = d.templateContainers?.some((c) => c.name === 'container2' && c.image === 'registry.k8s.io/pause:3.10');
          const term = (d.affinity as Record<string, any> | undefined)?.podAntiAffinity?.requiredDuringSchedulingIgnoredDuringExecution?.[0];
          return d.replicas === 3 && d.labels.id === 'very-important' && !!c1 && !!c2 && term?.topologyKey === 'kubernetes.io/hostname';
        },
      },
      {
        id: 'q7-netpol-egress',
        instruction:
          "**Question 7 | NetworkPolicy — egress**\n\nDans `project-snake`, applique `15_np.yaml` : la NetworkPolicy `np-backend` autorise les pods `backend-*` à joindre UNIQUEMENT `db1-*` sur `1111` et `db2-*` sur `2222`. Tout le reste (ex. `vault-*:3333`) est bloqué.",
        hint: 'kubectl apply -f 15_np.yaml\nkubectl -n project-snake describe netpol np-backend',
        validate: (s) => {
          const np = netpol(s, 'np-backend', 'project-snake');
          if (!np) return false;
          const okDb1 = np.egress.some((r) => r.podSelectors.some((ps) => ps.app === 'db1') && r.ports.some((p) => p.port === 1111));
          const okDb2 = np.egress.some((r) => r.podSelectors.some((ps) => ps.app === 'db2') && r.ports.some((p) => p.port === 2222));
          return np.podSelector.app === 'backend' && np.policyTypes.includes('Egress') && okDb1 && okDb2;
        },
      },
      {
        id: 'q8-cluster-rbac',
        instruction:
          "**Question 8 | RBAC cluster — lecture des nodes**\n\nL'équipe ops doit pouvoir consulter les nodes du cluster (ressource cluster-scoped).\n\n- Crée un ClusterRole `node-reader` : `get`, `list`, `watch` sur `nodes`\n- Crée un ClusterRoleBinding `ops-nodes` liant `node-reader` à l'utilisateur `ops`",
        hint: 'kubectl create clusterrole node-reader --verb=get,list,watch --resource=nodes\nkubectl create clusterrolebinding ops-nodes --clusterrole=node-reader --user=ops',
        validate: (s) => {
          const cr = s.clusterroles.find((x) => x.name === 'node-reader');
          const okCr = !!cr && hasAll(cr.verbs, ['get', 'list', 'watch']) && cr.resources.includes('nodes');
          const crb = s.clusterrolebindings.some((b) => b.name === 'ops-nodes' && b.roleName === 'node-reader' && b.subjects.some((u) => u.kind === 'User' && u.name === 'ops'));
          return okCr && crb;
        },
      },
      {
        id: 'q9-clusterrole-namespaced',
        instruction:
          "**Question 9 | RBAC — ClusterRole lié dans un namespace**\n\nLa développeuse `alice` doit consulter les workloads UNIQUEMENT dans `dev`. Standard : un ClusterRole réutilisable, lié localement.\n\n- Crée le namespace `dev`\n- Crée un ClusterRole `app-viewer` : `get`, `list`, `watch` sur `pods`, `services`, `deployments`\n- Dans `dev`, crée un RoleBinding `viewer` référençant le ClusterRole `app-viewer` pour l'utilisateur `alice`",
        hint: 'kubectl create namespace dev\nkubectl create clusterrole app-viewer --verb=get,list,watch --resource=pods,services,deployments\nkubectl -n dev create rolebinding viewer --clusterrole=app-viewer --user=alice',
        validate: (s) => {
          const cr = s.clusterroles.find((x) => x.name === 'app-viewer');
          const okCr = !!cr && hasAll(cr.verbs, ['get', 'list', 'watch']) && hasAll(cr.resources, ['pods', 'services', 'deployments']);
          const rb = s.rolebindings.some((b) => b.name === 'viewer' && b.namespace === 'dev' && b.roleKind === 'ClusterRole' && b.roleName === 'app-viewer' && b.subjects.some((u) => u.kind === 'User' && u.name === 'alice'));
          return s.namespaces.some((n) => n.name === 'dev') && okCr && rb;
        },
      },
      {
        id: 'q10-ha-workload',
        instruction:
          "**Question 10 | Workload haute disponibilité**\n\nMonte l'environnement de production, isolé et exposé.\n\n- Crée le namespace `production`\n- Déploie `frontend` (nginx:1.25) dans `production`\n- Expose-le en Service `NodePort` sur le port `80`\n- Absorbe un pic : porte `frontend` à `6` replicas",
        hint: 'kubectl create namespace production\nkubectl -n production create deployment frontend --image=nginx:1.25\nkubectl -n production expose deployment frontend --port=80 --type=NodePort\nkubectl -n production scale deployment frontend --replicas=6',
        validate: (s) => {
          const d = dep(s, 'frontend', 'production');
          const svc = s.services.some((v) => v.name === 'frontend' && v.namespace === 'production' && v.type === 'NodePort' && v.port === 80);
          return !!d && d.image === 'nginx:1.25' && d.replicas === 6 && svc;
        },
      },
      {
        id: 'q11-rollout',
        instruction:
          "**Question 11 | Rollout et rollback**\n\nLivre puis annule une mise à jour du service `web`.\n\n- Crée `web` en `nginx:1.24`\n- Mets à jour l'image du conteneur `web` vers `nginx:1.25`\n- La 1.25 est buguée : effectue un `rollout undo` pour revenir à `nginx:1.24`",
        hint: 'kubectl create deployment web --image=nginx:1.24\nkubectl set image deployment/web web=nginx:1.25\nkubectl rollout undo deployment/web',
        validate: (s) => {
          const d = dep(s, 'web');
          return !!d && d.image === 'nginx:1.24' && d.revision >= 3;
        },
      },
      {
        id: 'q12-config',
        instruction:
          "**Question 12 | Configuration — ConfigMap et Secret**\n\nProvisionne la configuration de l'application dans `ecommerce` (sans écrire de mot de passe en clair dans une image).\n\n- Crée le namespace `ecommerce`\n- ConfigMap `app-config` avec `LOG_LEVEL=debug`\n- Secret `db-cred` (generic) avec `password=s3cr3t`",
        hint: 'kubectl create namespace ecommerce\nkubectl -n ecommerce create configmap app-config --from-literal=LOG_LEVEL=debug\nkubectl -n ecommerce create secret generic db-cred --from-literal=password=s3cr3t',
        validate: (s) => {
          const cm = s.configmaps.find((c) => c.name === 'app-config' && c.namespace === 'ecommerce');
          const sec = s.secrets.find((c) => c.name === 'db-cred' && c.namespace === 'ecommerce');
          return !!cm && cm.data.LOG_LEVEL === 'debug' && !!sec && sec.data.password === 's3cr3t';
        },
      },
      {
        id: 'q13-sidecar',
        instruction:
          "**Question 13 | Pod multi-conteneurs (sidecar)**\n\nApplique `sidecar.yaml` : le Pod `webapp` (namespace `project-tiger`) porte le conteneur applicatif `app` (nginx:1.25) et le sidecar `log-agent` (busybox:1.36), et le label `tier=frontend`.",
        hint: 'kubectl apply -f sidecar.yaml\nkubectl -n project-tiger get pod webapp',
        validate: (s) => {
          const p = pod(s, 'webapp', 'project-tiger');
          if (!p) return false;
          const app = p.containers.some((c) => c.name === 'app' && c.image === 'nginx:1.25');
          const log = p.containers.some((c) => c.name === 'log-agent' && c.image === 'busybox:1.36');
          return p.containers.length === 2 && app && log && p.labels.tier === 'frontend';
        },
      },
      {
        id: 'q14-storageclass',
        instruction:
          "**Question 14 | StorageClass et PVC dynamique**\n\nManifestes `storageclass.yaml` et `data_pvc.yaml` fournis.\n\n- Crée la StorageClass `fast` (provisioner `kubernetes.io/no-provisioner`, `volumeBindingMode: WaitForFirstConsumer`)\n- Crée le PVC `data` dans `default` (`1Gi`, `ReadWriteOnce`, storageClassName `standard`) qui doit passer `Bound`",
        hint: 'kubectl apply -f storageclass.yaml\nkubectl apply -f data_pvc.yaml',
        validate: (s) => {
          const x = sc(s, 'fast');
          const okSc = !!x && x.provisioner === 'kubernetes.io/no-provisioner' && x.volumeBindingMode === 'WaitForFirstConsumer';
          const c = pvc(s, 'data', 'default');
          return okSc && !!c && c.status === 'Bound' && c.storageClassName === 'standard';
        },
      },
      {
        id: 'q15-netpol-ingress',
        instruction:
          "**Question 15 | NetworkPolicy — default-deny + allow**\n\nApplique le « zero trust » réseau sur `secure`. Manifestes `default-deny.yaml` et `allow-frontend.yaml` fournis.\n\n- Bloque tout l'ingress par défaut (`default-deny-ingress`, podSelector vide)\n- Autorise `app=frontend` à joindre `app=backend` (`allow-frontend`)",
        hint: 'kubectl create namespace secure\nkubectl apply -f default-deny.yaml\nkubectl apply -f allow-frontend.yaml',
        validate: (s) => {
          const deny = netpol(s, 'default-deny-ingress', 'secure');
          const okDeny = !!deny && deny.policyTypes.includes('Ingress') && Object.keys(deny.podSelector).length === 0;
          const allow = netpol(s, 'allow-frontend', 'secure');
          const okAllow = !!allow && allow.podSelector.app === 'backend' && allow.ingress.some((r) => r.podSelectors.some((ps) => ps.app === 'frontend'));
          return okDeny && okAllow;
        },
      },
      {
        id: 'q16-service-clusterip',
        instruction:
          "**Question 16 | Service interne**\n\nDéploie un cache et expose-le en interne.\n\n- Crée le namespace `data`\n- Déploie `cache` (redis:7) dans `data`\n- Expose `cache` en Service `ClusterIP` sur le port `6379`",
        hint: 'kubectl create namespace data\nkubectl -n data create deployment cache --image=redis:7\nkubectl -n data expose deployment cache --port=6379',
        validate: (s) => {
          const d = dep(s, 'cache', 'data');
          const svc = s.services.some((v) => v.name === 'cache' && v.namespace === 'data' && v.type === 'ClusterIP' && v.port === 6379);
          return !!d && d.image === 'redis:7' && svc;
        },
      },
      {
        id: 'q17-ci-group-rbac',
        instruction:
          "**Question 17 | RBAC — pipeline CI pour un groupe**\n\nAutorise tout le groupe CI à gérer les déploiements d'un namespace, via un ClusterRole réutilisable.\n\n- Crée le namespace `ci`\n- ClusterRole `deployer` : `create`, `get`, `list`, `update`, `patch` sur `deployments` et `replicasets`\n- Dans `ci`, RoleBinding `ci-deploy` liant le ClusterRole `deployer` au groupe `ci-team`",
        hint: 'kubectl create namespace ci\nkubectl create clusterrole deployer --verb=create,get,list,update,patch --resource=deployments,replicasets\nkubectl -n ci create rolebinding ci-deploy --clusterrole=deployer --group=ci-team',
        validate: (s) => {
          const cr = s.clusterroles.find((x) => x.name === 'deployer');
          const okCr = !!cr && hasAll(cr.verbs, ['create', 'get', 'list', 'update', 'patch']) && hasAll(cr.resources, ['deployments', 'replicasets']);
          const rb = s.rolebindings.some((b) => b.name === 'ci-deploy' && b.namespace === 'ci' && b.roleKind === 'ClusterRole' && b.roleName === 'deployer' && b.subjects.some((u) => u.kind === 'Group' && u.name === 'ci-team'));
          return s.namespaces.some((n) => n.name === 'ci') && okCr && rb;
        },
      },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────────
  // CKA Simulator B — deuxième grand examen de simulation (17 questions),
  // distinct du Simulator A. Mêmes contraintes : seules les questions
  // reproductibles dans le moteur sont retenues (les questions purement
  // niveau-nœud — kubelet/certs, etcd, static pod scheduling, api-resources,
  // events/crictl, kustomize/operator — sont omises ou adaptées).
  {
    id: 'cka-simulator-b',
    cert: CKA,
    title: 'CKA Simulator B — Kubernetes 1.35',
    level: 'CKA',
    summary: "Deuxième examen de simulation : 17 questions — DNS/FQDN, multi-conteneurs, Secrets montés, PV/PVC dynamique, sondes, StatefulSet, rollout, HPA, RBAC, DaemonSet, topologie et NetworkPolicy.",
    scenario:
      "CKA Simulator B — 17 questions, un seul environnement.\n\n" +
      "Deuxième session type killer.sh, différente du Simulator A. Enchaînez les questions dans le même terminal, chacune notée séparément. Marquez celles à revoir et surveillez le temps.\n\n" +
      "Les ressources supposées « déjà en place » sont pré-provisionnées ; les manifestes fournis sont dans le répertoire courant (ls pour les lister). Astuce : kubectl accepte le flag de namespace avant OU après le verbe.",
    seed: [
      // Q1 : ConfigMap control-config existant (valeurs DNS incorrectes à corriger)
      ['apiVersion: v1', 'kind: ConfigMap', 'metadata:', '  name: control-config', '  namespace: lima-control',
       'data:', '  DNS_1: wrong', '  DNS_2: wrong', '  DNS_3: wrong', '  DNS_4: wrong'].join('\n'),
      // Q9 : Deployment web avec resources.requests (cible du HPA)
      ['apiVersion: apps/v1', 'kind: Deployment', 'metadata:', '  name: web', '  namespace: default',
       'spec:', '  replicas: 1', '  selector:', '    matchLabels:', '      app: web', '  template:', '    metadata:',
       '      labels:', '        app: web', '    spec:', '      containers:', '      - name: nginx', '        image: nginx:1.25',
       '        resources:', '          requests:', '            cpu: 50m', '            memory: 64Mi'].join('\n'),
    ],
    files: [
      { path: 'control-config.yaml', content: ['apiVersion: v1', 'kind: ConfigMap', 'metadata:', '  name: control-config', '  namespace: lima-control', 'data:',
        '  DNS_1: kubernetes.default.svc.cluster.local',
        '  DNS_2: department.lima-workload.svc.cluster.local',
        '  DNS_3: section100.section.lima-workload.svc.cluster.local',
        '  DNS_4: 1-2-3-4.kube-system.pod.cluster.local'].join('\n') },
      { path: 'static-svc.yaml', content: ['apiVersion: v1', 'kind: Service', 'metadata:', '  name: static-pod-service', '  namespace: default', 'spec:', '  type: NodePort', '  selector:', '    run: my-static-pod', '  ports:', '  - port: 80', '    targetPort: 80'].join('\n') },
      { path: 'ready-pod.yaml', content: ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: ready-if-service-ready', '  namespace: default', '  labels:', '    run: ready-if-service-ready', 'spec:', '  containers:', '  - name: c', '    image: nginx:1-alpine',
        '    livenessProbe:', '      exec:', '        command: ["true"]',
        '    readinessProbe:', '      exec:', '        command: ["sh", "-c", "wget -T2 -O- http://service-am-i-ready:80"]'].join('\n') },
      { path: 'multi.yaml', content: ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: multi-container-playground', '  namespace: default', 'spec:',
        '  volumes:', '  - name: vol', '    emptyDir: {}',
        '  containers:',
        '  - name: c1', '    image: nginx:1-alpine', '    volumeMounts:', '    - name: vol', '      mountPath: /vol',
        '  - name: c2', '    image: busybox:1', '    command: ["sh", "-c", "while true; do date >> /vol/date.log; sleep 1; done"]', '    volumeMounts:', '    - name: vol', '      mountPath: /vol',
        '  - name: c3', '    image: busybox:1', '    command: ["sh", "-c", "tail -f /vol/date.log"]', '    volumeMounts:', '    - name: vol', '      mountPath: /vol'].join('\n') },
      { path: 'secret-pod.yaml', content: ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: secret-pod', '  namespace: secret', 'spec:',
        '  volumes:', '  - name: secret1', '    secret:', '      secretName: secret1',
        '  containers:', '  - name: secret-pod', '    image: busybox:1', '    args: ["sh", "-c", "sleep 1d"]',
        '    volumeMounts:', '    - name: secret1', '      mountPath: /tmp/secret1', '      readOnly: true'].join('\n') },
      { path: 'sc.yaml', content: ['apiVersion: storage.k8s.io/v1', 'kind: StorageClass', 'metadata:', '  name: local-backup', 'provisioner: rancher.io/local-path', 'reclaimPolicy: Retain', 'volumeBindingMode: WaitForFirstConsumer'].join('\n') },
      { path: 'backup-pvc.yaml', content: ['apiVersion: v1', 'kind: PersistentVolumeClaim', 'metadata:', '  name: backup-pvc', '  namespace: project-bern', 'spec:', '  accessModes:', '  - ReadWriteOnce', '  storageClassName: local-backup', '  resources:', '    requests:', '      storage: 50Mi'].join('\n') },
      { path: 'backup-pod.yaml', content: ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: backup-runner', '  namespace: project-bern', 'spec:', '  volumes:', '  - name: backup', '    persistentVolumeClaim:', '      claimName: backup-pvc', '  containers:', '  - name: bash', '    image: bash:5', '    volumeMounts:', '    - name: backup', '      mountPath: /backup'].join('\n') },
      { path: 'sts.yaml', content: ['apiVersion: apps/v1', 'kind: StatefulSet', 'metadata:', '  name: web', '  namespace: lima-workload', 'spec:', '  serviceName: web', '  replicas: 3', '  selector:', '    matchLabels:', '      app: web', '  template:', '    metadata:', '      labels:', '        app: web', '    spec:', '      containers:', '      - name: nginx', '        image: nginx:1-alpine'].join('\n') },
      { path: 'sts-svc.yaml', content: ['apiVersion: v1', 'kind: Service', 'metadata:', '  name: web', '  namespace: lima-workload', 'spec:', '  clusterIP: None', '  selector:', '    app: web', '  ports:', '  - port: 80', '    targetPort: 80'].join('\n') },
      { path: 'spread.yaml', content: ['apiVersion: apps/v1', 'kind: Deployment', 'metadata:', '  name: spread-app', '  namespace: default', '  labels:', '    app: spread-app', 'spec:', '  replicas: 4', '  selector:', '    matchLabels:', '      app: spread-app', '  template:', '    metadata:', '      labels:', '        app: spread-app', '    spec:', '      containers:', '      - name: nginx', '        image: nginx:1-alpine', '      topologySpreadConstraints:', '      - maxSkew: 1', '        topologyKey: kubernetes.io/hostname', '        whenUnsatisfiable: DoNotSchedule', '        labelSelector:', '          matchLabels:', '            app: spread-app'].join('\n') },
      { path: 'db-netpol.yaml', content: ['apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: db-allow', '  namespace: data', 'spec:', '  podSelector:', '    matchLabels:', '      app: db', '  policyTypes:', '  - Ingress', '  ingress:', '  - from:', '    - podSelector:', '        matchLabels:', '          app: api', '    ports:', '    - protocol: TCP', '      port: 5432'].join('\n') },
      { path: 'log-ds.yaml', content: ['apiVersion: apps/v1', 'kind: DaemonSet', 'metadata:', '  name: log-agent', '  namespace: kube-system', 'spec:', '  selector:', '    matchLabels:', '      app: log-agent', '  template:', '    metadata:', '      labels:', '        app: log-agent', '    spec:', '      containers:', '      - name: agent', '        image: busybox:1', '        resources:', '          requests:', '            cpu: 20m', '            memory: 32Mi', '      tolerations:', '      - effect: NoSchedule', '        key: node-role.kubernetes.io/control-plane'].join('\n') },
      { path: 'app-settings.yaml', content: ['apiVersion: v1', 'kind: ConfigMap', 'metadata:', '  name: app-settings', '  namespace: default', 'data:', '  LOG_LEVEL: info', '  MAX_CONN: "100"'].join('\n') },
    ],
    tasks: [
      {
        id: 'q1-dns-configmap',
        instruction:
          "**Question 1 | DNS / FQDN**\n\nLe Deployment `controller` (namespace `lima-control`) lit ses endpoints via des FQDN dans la ConfigMap `control-config`. Applique `control-config.yaml` pour corriger les 4 valeurs :\n\n- `DNS_1` : Service `kubernetes` (ns `default`)\n- `DNS_2` : Service headless `department` (ns `lima-workload`)\n- `DNS_3` : Pod `section100` derrière le Service `section` (ns `lima-workload`)\n- `DNS_4` : un Pod d'IP `1.2.3.4` (ns `kube-system`)",
        hint: 'cat control-config.yaml\nkubectl apply -f control-config.yaml',
        validate: (s) => {
          const cm = s.configmaps.find((c) => c.name === 'control-config' && c.namespace === 'lima-control');
          return !!cm && cm.data.DNS_1 === 'kubernetes.default.svc.cluster.local' && cm.data.DNS_2 === 'department.lima-workload.svc.cluster.local' && cm.data.DNS_3 === 'section100.section.lima-workload.svc.cluster.local' && cm.data.DNS_4 === '1-2-3-4.kube-system.pod.cluster.local';
        },
      },
      {
        id: 'q2-pod-nodeport',
        instruction:
          "**Question 2 | Pod exposé en NodePort**\n\nDans `default` :\n\n- Crée un Pod `my-static-pod` d'image `nginx:1-alpine`\n- Applique `static-svc.yaml` : un Service NodePort `static-pod-service` qui expose ce Pod sur le port `80` (sélecteur `run=my-static-pod`)",
        hint: 'kubectl run my-static-pod --image=nginx:1-alpine\nkubectl apply -f static-svc.yaml',
        validate: (s) => {
          const p = pod(s, 'my-static-pod', 'default');
          const svc = s.services.find((v) => v.name === 'static-pod-service' && v.namespace === 'default');
          return !!p && p.containers[0]?.image === 'nginx:1-alpine' && !!svc && svc.type === 'NodePort' && svc.port === 80 && svc.selector.run === 'my-static-pod';
        },
      },
      {
        id: 'q3-probes',
        instruction:
          "**Question 3 | Pod prêt si un Service est joignable**\n\nApplique `ready-pod.yaml` : le Pod `ready-if-service-ready` (nginx:1-alpine) a une `livenessProbe` exec `true` et une `readinessProbe` exec `wget` sur `http://service-am-i-ready:80`. Crée ensuite le Pod `am-i-ready` (nginx:1-alpine) avec le label `id=cross-server-ready`.",
        hint: 'kubectl apply -f ready-pod.yaml\nkubectl run am-i-ready --image=nginx:1-alpine --labels="id=cross-server-ready"',
        validate: (s) => {
          const p = pod(s, 'ready-if-service-ready', 'default');
          const live = p?.containers[0]?.livenessProbe;
          const ready = p?.containers[0]?.readinessProbe;
          const amiready = pod(s, 'am-i-ready', 'default');
          return !!live && live.kind === 'exec' && !!ready && ready.kind === 'exec' && !!amiready && amiready.labels.id === 'cross-server-ready';
        },
      },
      {
        id: 'q4-multi-container',
        instruction:
          "**Question 4 | Multi-conteneurs et volume partagé**\n\nApplique `multi.yaml` : le Pod `multi-container-playground` a un volume `emptyDir` monté dans 3 conteneurs — `c1` (nginx:1-alpine), `c2` (busybox:1, écrit `date` dans `/vol/date.log`), `c3` (busybox:1, `tail -f`).",
        hint: 'kubectl apply -f multi.yaml\nkubectl logs multi-container-playground -c c3',
        validate: (s) => {
          const p = pod(s, 'multi-container-playground', 'default');
          if (!p || p.containers.length !== 3) return false;
          const vol = (p.volumes || []).some((v) => v.kind === 'emptyDir' && v.name === 'vol');
          const c1 = p.containers.some((c) => c.name === 'c1' && c.image === 'nginx:1-alpine');
          const c2 = p.containers.some((c) => c.name === 'c2' && (c.command || []).join(' ').includes('date'));
          const c3 = p.containers.some((c) => c.name === 'c3' && (c.command || []).join(' ').includes('tail'));
          const mounts = p.containers.every((c) => c.volumeMounts?.some((m) => m.name === 'vol' && m.mountPath === '/vol'));
          return vol && c1 && c2 && c3 && mounts;
        },
      },
      {
        id: 'q5-secret-mount',
        instruction:
          "**Question 5 | Secret monté en lecture seule**\n\n- Crée le namespace `secret`\n- Crée un Secret `secret1` (`halt=stop`) dans `secret`\n- Applique `secret-pod.yaml` : le Pod `secret-pod` (busybox:1, `sleep 1d`) monte `secret1` en lecture seule sur `/tmp/secret1`",
        hint: 'kubectl create namespace secret\nkubectl -n secret create secret generic secret1 --from-literal=halt=stop\nkubectl apply -f secret-pod.yaml',
        validate: (s) => {
          const sec = s.secrets.some((x) => x.name === 'secret1' && x.namespace === 'secret');
          const p = pod(s, 'secret-pod', 'secret');
          const vol = (p?.volumes || []).some((v) => v.kind === 'secret' && v.source === 'secret1');
          const mount = p?.containers[0]?.volumeMounts?.some((m) => m.mountPath === '/tmp/secret1' && m.readOnly === true);
          return sec && !!p && vol && !!mount;
        },
      },
      {
        id: 'q6-dynamic-pv',
        instruction:
          "**Question 6 | Provisionnement dynamique (Retain)**\n\n- Applique `sc.yaml` : StorageClass `local-backup` (`rancher.io/local-path`, `WaitForFirstConsumer`, `Retain`)\n- Applique `backup-pvc.yaml` : PVC `backup-pvc` (`50Mi`) dans `project-bern`\n- Applique `backup-pod.yaml` : le Pod qui monte le PVC déclenche le provisionnement → le PVC passe `Bound`",
        hint: 'kubectl apply -f sc.yaml\nkubectl apply -f backup-pvc.yaml\nkubectl apply -f backup-pod.yaml',
        validate: (s) => {
          const x = sc(s, 'local-backup');
          const okSc = !!x && x.provisioner === 'rancher.io/local-path' && x.volumeBindingMode === 'WaitForFirstConsumer' && x.reclaimPolicy === 'Retain';
          const c = pvc(s, 'backup-pvc', 'project-bern');
          return okSc && !!c && c.status === 'Bound';
        },
      },
      {
        id: 'q7-statefulset',
        instruction:
          "**Question 7 | StatefulSet et Service headless**\n\nDans `lima-workload` :\n\n- Applique `sts.yaml` : StatefulSet `web` (nginx:1-alpine, `3` replicas, `serviceName: web`)\n- Applique `sts-svc.yaml` : le Service headless `web` (`clusterIP: None`)",
        hint: 'kubectl apply -f sts.yaml\nkubectl apply -f sts-svc.yaml',
        validate: (s) => {
          const st = sts(s, 'web', 'lima-workload');
          const svc = s.services.some((v) => v.name === 'web' && v.namespace === 'lima-workload');
          return !!st && st.replicas === 3 && st.image === 'nginx:1-alpine' && st.serviceName === 'web' && svc;
        },
      },
      {
        id: 'q8-rollout',
        instruction:
          "**Question 8 | Mises à jour successives et rollback**\n\nDans `default` :\n\n- Crée `app` en `nginx:1.20`\n- Mets à jour vers `nginx:1.21`, puis vers `nginx:1.22`\n- La `1.22` casse : `rollout undo` pour revenir à `nginx:1.21`",
        hint: 'kubectl create deployment app --image=nginx:1.20\nkubectl set image deployment/app nginx=nginx:1.21\nkubectl set image deployment/app nginx=nginx:1.22\nkubectl rollout undo deployment/app',
        validate: (s) => {
          const d = dep(s, 'app', 'default');
          return !!d && d.image === 'nginx:1.21' && d.revision >= 4;
        },
      },
      {
        id: 'q9-hpa',
        instruction:
          "**Question 9 | HorizontalPodAutoscaler (impératif)**\n\nLe Deployment `web` (namespace `default`) expose des requests CPU. Crée un HPA le ciblant : min `3`, max `9`, à `70%` d'utilisation CPU.",
        hint: 'kubectl autoscale deployment web --min=3 --max=9 --cpu-percent=70',
        validate: (s) => {
          const h = hpa(s, 'web', 'default');
          return !!h && h.targetName === 'web' && h.minReplicas === 3 && h.maxReplicas === 9 && h.cpuUtilization === 70;
        },
      },
      {
        id: 'q10-rbac-controller',
        instruction:
          "**Question 10 | RBAC — contrôleur endpoints**\n\nDans `lima-control`, un contrôleur observe services et endpoints.\n\n- ServiceAccount `endpoint-controller`\n- Role `ep-reader` : `get`, `list`, `watch` sur `services` et `endpoints`\n- RoleBinding `ep-bind` liant les deux",
        hint: 'kubectl -n lima-control create sa endpoint-controller\nkubectl -n lima-control create role ep-reader --verb=get,list,watch --resource=services,endpoints\nkubectl -n lima-control create rolebinding ep-bind --role=ep-reader --serviceaccount=lima-control:endpoint-controller',
        validate: (s) => {
          const r = role(s, 'ep-reader', 'lima-control');
          const okR = !!r && hasAll(r.verbs, ['get', 'list', 'watch']) && hasAll(r.resources, ['services', 'endpoints']);
          return s.serviceaccounts.some((a) => a.name === 'endpoint-controller' && a.namespace === 'lima-control') && okR && s.rolebindings.some((b) => b.name === 'ep-bind' && b.namespace === 'lima-control' && b.roleName === 'ep-reader');
        },
      },
      {
        id: 'q11-daemonset',
        instruction:
          "**Question 11 | DaemonSet de journalisation**\n\nApplique `log-ds.yaml` : le DaemonSet `log-agent` (namespace `kube-system`, image `busybox:1`) demande `20m` CPU / `32Mi` mémoire et tolère le taint des control-plane (`node-role.kubernetes.io/control-plane`).",
        hint: 'kubectl apply -f log-ds.yaml\nkubectl -n kube-system get ds log-agent',
        validate: (s) => {
          const d = ds(s, 'log-agent', 'kube-system');
          if (!d) return false;
          const okReq = d.requests?.cpu === '20m' && d.requests?.memory === '32Mi';
          const okTol = (d.tolerations || []).some((t) => t.key === 'node-role.kubernetes.io/control-plane');
          return d.image === 'busybox:1' && okReq && okTol;
        },
      },
      {
        id: 'q12-topology-spread',
        instruction:
          "**Question 12 | Répartition topologique**\n\nApplique `spread.yaml` : le Deployment `spread-app` (4 replicas) utilise un `topologySpreadConstraints` (`maxSkew: 1`, `topologyKey: kubernetes.io/hostname`, `whenUnsatisfiable: DoNotSchedule`).",
        hint: 'kubectl apply -f spread.yaml',
        validate: (s) => {
          const d = dep(s, 'spread-app', 'default');
          if (!d || d.replicas !== 4) return false;
          const t = (d.topologySpreadConstraints as Array<Record<string, unknown>> | undefined)?.[0];
          return !!t && t.topologyKey === 'kubernetes.io/hostname' && t.maxSkew === 1;
        },
      },
      {
        id: 'q13-netpol-db',
        instruction:
          "**Question 13 | NetworkPolicy — accès base de données**\n\nApplique `db-netpol.yaml` dans `data` : la NetworkPolicy `db-allow` autorise l'ingress vers `app=db` UNIQUEMENT depuis `app=api` sur le port `5432`.",
        hint: 'kubectl apply -f db-netpol.yaml',
        validate: (s) => {
          const np = netpol(s, 'db-allow', 'data');
          return !!np && np.podSelector.app === 'db' && np.policyTypes.includes('Ingress') && np.ingress.some((r) => r.podSelectors.some((ps) => ps.app === 'api') && r.ports.some((p) => p.port === 5432));
        },
      },
      {
        id: 'q14-configmap-multi',
        instruction:
          "**Question 14 | ConfigMap multi-clés**\n\nApplique `app-settings.yaml` : la ConfigMap `app-settings` (namespace `default`) contient `LOG_LEVEL=info` et `MAX_CONN=100`.",
        hint: 'kubectl apply -f app-settings.yaml',
        validate: (s) => {
          const cm = s.configmaps.find((c) => c.name === 'app-settings' && c.namespace === 'default');
          return !!cm && cm.data.LOG_LEVEL === 'info' && cm.data.MAX_CONN === '100';
        },
      },
      {
        id: 'q15-service-targetport',
        instruction:
          "**Question 15 | Service avec targetPort distinct**\n\nDans `default` :\n\n- Déploie `api` (image `nginx:1-alpine`)\n- Expose-le en Service `ClusterIP` sur le port `8080` qui redirige vers le `targetPort` `80`",
        hint: 'kubectl create deployment api --image=nginx:1-alpine\nkubectl expose deployment api --port=8080 --target-port=80',
        validate: (s) => {
          const svc = s.services.find((v) => v.name === 'api' && v.namespace === 'default');
          return !!dep(s, 'api', 'default') && !!svc && svc.port === 8080 && svc.targetPort === 80 && svc.type === 'ClusterIP';
        },
      },
      {
        id: 'q16-scale-expose',
        instruction:
          "**Question 16 | Déployer, dimensionner, exposer**\n\nDans le namespace `messaging` :\n\n- Déploie `queue` (image `redis:7`)\n- Monte-le à `4` replicas\n- Expose-le en `ClusterIP` sur le port `6379`",
        hint: 'kubectl create namespace messaging\nkubectl -n messaging create deployment queue --image=redis:7\nkubectl -n messaging scale deployment queue --replicas=4\nkubectl -n messaging expose deployment queue --port=6379',
        validate: (s) => {
          const d = dep(s, 'queue', 'messaging');
          const svc = s.services.some((v) => v.name === 'queue' && v.namespace === 'messaging' && v.port === 6379);
          return !!d && d.image === 'redis:7' && d.replicas === 4 && svc;
        },
      },
      {
        id: 'q17-cluster-rbac',
        instruction:
          "**Question 17 | RBAC cluster — SRE en lecture seule**\n\nLe groupe SRE doit consulter (lecture seule) pods, nodes et namespaces sur tout le cluster.\n\n- ClusterRole `cluster-viewer` : `get`, `list`, `watch` sur `pods`, `nodes`, `namespaces`\n- ClusterRoleBinding `sre-view` liant `cluster-viewer` au groupe `sre`",
        hint: 'kubectl create clusterrole cluster-viewer --verb=get,list,watch --resource=pods,nodes,namespaces\nkubectl create clusterrolebinding sre-view --clusterrole=cluster-viewer --group=sre',
        validate: (s) => {
          const cr = s.clusterroles.find((x) => x.name === 'cluster-viewer');
          const okCr = !!cr && hasAll(cr.verbs, ['get', 'list', 'watch']) && hasAll(cr.resources, ['pods', 'nodes', 'namespaces']);
          const crb = s.clusterrolebindings.some((b) => b.name === 'sre-view' && b.roleName === 'cluster-viewer' && b.subjects.some((u) => u.kind === 'Group' && u.name === 'sre'));
          return okCr && crb;
        },
      },
    ],
  },
  // ════════════════════════════════════════════════════════════════ CKS
  // ─────────────────────────────────────────────────────────────────────────
  // CKS Simulator A — grand examen de simulation type killer.sh : 15 questions.
  // Portée réellement simulable pour CKS dans ce moteur : NetworkPolicy
  // (default-deny, ingress/egress ciblés, ports), RBAC de moindre privilège,
  // Secrets (vs ConfigMap, montage, accès restreint), ServiceAccounts. Les
  // sujets non simulables (securityContext/PSA, seccomp/AppArmor, image
  // scanning, Falco, audit policy, runtime) sont omis.
  {
    id: 'cks-simulator-a',
    cert: CKS,
    title: 'CKS Simulator A — Kubernetes 1.35',
    level: 'CKS',
    summary: "Examen de simulation sécurité : 15 questions — NetworkPolicy (default-deny, ingress/egress), RBAC moindre privilège, Secrets et ServiceAccounts.",
    scenario:
      "CKS Simulator A — 15 questions, un seul environnement.\n\n" +
      "Format killer.sh : enchaînez les questions dans le même terminal, chacune notée séparément. Le fil rouge du CKS : réduire la surface d'attaque — couper le trafic par défaut puis n'ouvrir que le nécessaire, et n'accorder que le strict minimum de droits.\n\n" +
      "Les ressources supposées « déjà en place » (pods à isoler) sont pré-provisionnées ; les manifestes fournis sont dans le répertoire courant (ls pour les lister). Astuce : kubectl accepte le flag de namespace avant OU après le verbe.",
    seed: [
      // Cibles egress de la Q6 (namespace « data »). Le namespace « secure » est
      // volontairement NON pré-créé : la Q1 le crée.
      ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: db1-0', '  namespace: data', '  labels:', '    app: db1', 'spec:', '  containers:', '  - name: nginx', '    image: nginx:1-alpine'].join('\n'),
      ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: db2-0', '  namespace: data', '  labels:', '    app: db2', 'spec:', '  containers:', '  - name: nginx', '    image: nginx:1-alpine'].join('\n'),
    ],
    files: [
      { path: 'deny-ingress.yaml', content: ['apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: default-deny-ingress', '  namespace: secure', 'spec:', '  podSelector: {}', '  policyTypes:', '  - Ingress'].join('\n') },
      { path: 'allow-frontend.yaml', content: ['apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: allow-frontend', '  namespace: secure', 'spec:', '  podSelector:', '    matchLabels:', '      app: backend', '  policyTypes:', '  - Ingress', '  ingress:', '  - from:', '    - podSelector:', '        matchLabels:', '          app: frontend'].join('\n') },
      { path: 'deny-egress.yaml', content: ['apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: default-deny-egress', '  namespace: secure', 'spec:', '  podSelector: {}', '  policyTypes:', '  - Egress'].join('\n') },
      { path: 'backend-egress.yaml', content: ['apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: backend-egress', '  namespace: data', 'spec:', '  podSelector:', '    matchLabels:', '      app: backend', '  policyTypes:', '  - Egress', '  egress:', '  - to:', '    - podSelector:', '        matchLabels:', '          app: db1', '    ports:', '    - protocol: TCP', '      port: 1111', '  - to:', '    - podSelector:', '        matchLabels:', '          app: db2', '    ports:', '    - protocol: TCP', '      port: 2222'].join('\n') },
      { path: 'secret-mount.yaml', content: ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: sec-app', '  namespace: secure', '  labels:', '    app: sec-app', 'spec:', '  volumes:', '  - name: creds', '    secret:', '      secretName: db-creds', '  containers:', '  - name: app', '    image: nginx:1-alpine', '    volumeMounts:', '    - name: creds', '      mountPath: /etc/creds', '      readOnly: true'].join('\n') },
      { path: 'both-ways.yaml', content: ['apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: backend-lockdown', '  namespace: secure', 'spec:', '  podSelector:', '    matchLabels:', '      app: backend', '  policyTypes:', '  - Ingress', '  - Egress', '  ingress:', '  - from:', '    - podSelector:', '        matchLabels:', '          app: frontend'].join('\n') },
      { path: 'three-tier.yaml', content: [
        'apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: allow-web-to-api', '  namespace: secure',
        'spec:', '  podSelector:', '    matchLabels:', '      app: api', '  policyTypes:', '  - Ingress',
        '  ingress:', '  - from:', '    - podSelector:', '        matchLabels:', '          app: web',
        '---',
        'apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: allow-api-to-db', '  namespace: secure',
        'spec:', '  podSelector:', '    matchLabels:', '      app: db', '  policyTypes:', '  - Ingress',
        '  ingress:', '  - from:', '    - podSelector:', '        matchLabels:', '          app: api',
      ].join('\n') },
      { path: 'api-cache.yaml', content: [
        'apiVersion: networking.k8s.io/v1', 'kind: NetworkPolicy', 'metadata:', '  name: api-to-cache', '  namespace: secure',
        'spec:', '  podSelector:', '    matchLabels:', '      app: api', '  policyTypes:', '  - Egress',
        '  egress:', '  - to:', '    - podSelector:', '        matchLabels:', '          app: cache', '    ports:', '    - protocol: TCP', '      port: 6379',
      ].join('\n') },
    ],
    tasks: [
      {
        id: 'q1-ns-secret',
        instruction:
          "**Question 1 | Secret plutôt qu'en clair**\n\nUne charge sensible doit lire ses identifiants sans les coder en dur dans l'image.\n\n- Crée le namespace `secure`\n- Crée un Secret générique `db-creds` avec `PASSWORD=s3cr3t` dans `secure`",
        hint: 'kubectl create namespace secure\nkubectl -n secure create secret generic db-creds --from-literal=PASSWORD=s3cr3t',
        validate: (s) => s.namespaces.some((n) => n.name === 'secure') && s.secrets.some((x) => x.name === 'db-creds' && x.namespace === 'secure' && x.data.PASSWORD === 's3cr3t'),
      },
      {
        id: 'q2-rbac-secret-reader',
        instruction:
          "**Question 2 | RBAC — lecture de secret au strict minimum**\n\nDans `secure`, la charge ne doit pouvoir QUE lire les secrets.\n\n- Crée un ServiceAccount `app-sa`\n- Crée un Role `secret-reader` : verbe `get` sur `secrets`\n- Lie-le à `app-sa` via un RoleBinding `app-secret`",
        hint: 'kubectl -n secure create sa app-sa\nkubectl -n secure create role secret-reader --verb=get --resource=secrets\nkubectl -n secure create rolebinding app-secret --role=secret-reader --serviceaccount=secure:app-sa',
        validate: (s) => {
          const sa = s.serviceaccounts.some((a) => a.name === 'app-sa' && a.namespace === 'secure');
          const r = role(s, 'secret-reader', 'secure');
          const okRole = !!r && r.verbs.includes('get') && (r.resources.includes('secrets') || r.resources.includes('secret')) && !r.verbs.includes('list') && !r.verbs.includes('*');
          const rb = s.rolebindings.some((b) => b.name === 'app-secret' && b.namespace === 'secure' && b.roleName === 'secret-reader' && b.subjects.some((u) => u.name === 'app-sa' && u.namespace === 'secure'));
          return sa && okRole && rb;
        },
      },
      {
        id: 'q3-deny-ingress',
        instruction:
          "**Question 3 | Zero-trust — bloquer tout l'ingress**\n\nApplique `deny-ingress.yaml` : une NetworkPolicy `default-deny-ingress` (podSelector vide) qui coupe TOUT le trafic entrant du namespace `secure`.",
        hint: 'kubectl apply -f deny-ingress.yaml',
        validate: (s) => {
          const np = netpol(s, 'default-deny-ingress', 'secure');
          return !!np && Object.keys(np.podSelector).length === 0 && np.policyTypes.includes('Ingress');
        },
      },
      {
        id: 'q4-allow-frontend',
        instruction:
          "**Question 4 | N'ouvrir que le nécessaire**\n\nApplique `allow-frontend.yaml` : autorise l'ingress vers les pods `app=backend` UNIQUEMENT depuis les pods `app=frontend`.",
        hint: 'kubectl apply -f allow-frontend.yaml',
        validate: (s) => {
          const np = netpol(s, 'allow-frontend', 'secure');
          return !!np && np.podSelector.app === 'backend' && np.policyTypes.includes('Ingress') && np.ingress.some((r) => r.podSelectors.some((ps) => ps.app === 'frontend'));
        },
      },
      {
        id: 'q5-deny-egress',
        instruction:
          "**Question 5 | Couper l'egress par défaut**\n\nApplique `deny-egress.yaml` : une NetworkPolicy `default-deny-egress` (podSelector vide, policyType Egress) sur `secure`.",
        hint: 'kubectl apply -f deny-egress.yaml',
        validate: (s) => {
          const np = netpol(s, 'default-deny-egress', 'secure');
          return !!np && Object.keys(np.podSelector).length === 0 && np.policyTypes.includes('Egress');
        },
      },
      {
        id: 'q6-backend-egress',
        instruction:
          "**Question 6 | Egress ciblé vers les bases**\n\nApplique `backend-egress.yaml` dans `data` : les pods `app=backend` ne peuvent joindre QUE `db1` sur `1111` et `db2` sur `2222`.",
        hint: 'kubectl apply -f backend-egress.yaml\nkubectl -n data describe netpol backend-egress',
        validate: (s) => {
          const np = netpol(s, 'backend-egress', 'data');
          if (!np) return false;
          const okDb1 = np.egress.some((r) => r.podSelectors.some((ps) => ps.app === 'db1') && r.ports.some((p) => p.port === 1111));
          const okDb2 = np.egress.some((r) => r.podSelectors.some((ps) => ps.app === 'db2') && r.ports.some((p) => p.port === 2222));
          return np.podSelector.app === 'backend' && np.policyTypes.includes('Egress') && okDb1 && okDb2;
        },
      },
      {
        id: 'q7-config-vs-secret',
        instruction:
          "**Question 7 | Séparer le sensible du non-sensible**\n\nDans `secure` : le non-sensible en ConfigMap, le sensible en Secret.\n\n- ConfigMap `app-config` avec `LOG_LEVEL=info`\n- Secret `api-token` avec `TOKEN=abc123`",
        hint: 'kubectl -n secure create configmap app-config --from-literal=LOG_LEVEL=info\nkubectl -n secure create secret generic api-token --from-literal=TOKEN=abc123',
        validate: (s) => {
          const cm = s.configmaps.find((c) => c.name === 'app-config' && c.namespace === 'secure');
          const sec = s.secrets.find((c) => c.name === 'api-token' && c.namespace === 'secure');
          return !!cm && cm.data.LOG_LEVEL === 'info' && !!sec && sec.data.TOKEN === 'abc123';
        },
      },
      {
        id: 'q8-audit-readonly',
        instruction:
          "**Question 8 | Accès auditeur en lecture seule**\n\nUn auditeur doit consulter (sans modifier) les workloads de `secure`.\n\n- Crée un ClusterRole `viewer` : `get`, `list`, `watch` sur `pods`, `services`, `deployments`\n- Dans `secure`, RoleBinding `audit-view` liant `viewer` à l'utilisateur `auditor`",
        hint: 'kubectl create clusterrole viewer --verb=get,list,watch --resource=pods,services,deployments\nkubectl -n secure create rolebinding audit-view --clusterrole=viewer --user=auditor',
        validate: (s) => {
          const cr = s.clusterroles.find((x) => x.name === 'viewer');
          const okCr = !!cr && hasAll(cr.verbs, ['get', 'list', 'watch']) && hasAll(cr.resources, ['pods', 'services', 'deployments']);
          const rb = s.rolebindings.some((b) => b.name === 'audit-view' && b.namespace === 'secure' && b.roleKind === 'ClusterRole' && b.roleName === 'viewer' && b.subjects.some((u) => u.kind === 'User' && u.name === 'auditor'));
          return okCr && rb;
        },
      },
      {
        id: 'q9-pod-reader-sa',
        instruction:
          "**Question 9 | SA de supervision restreint**\n\nDans `secure`, un SA de monitoring ne doit voir que les pods.\n\n- ServiceAccount `monitor`\n- Role `pod-reader` : `get`, `list` sur `pods` (rien d'autre)\n- RoleBinding `monitor-pods` liant les deux",
        hint: 'kubectl -n secure create sa monitor\nkubectl -n secure create role pod-reader --verb=get,list --resource=pods\nkubectl -n secure create rolebinding monitor-pods --role=pod-reader --serviceaccount=secure:monitor',
        validate: (s) => {
          const r = role(s, 'pod-reader', 'secure');
          const okR = !!r && hasAll(r.verbs, ['get', 'list']) && r.resources.includes('pods') && !r.resources.includes('secrets');
          return s.serviceaccounts.some((a) => a.name === 'monitor' && a.namespace === 'secure') && okR && s.rolebindings.some((b) => b.name === 'monitor-pods' && b.namespace === 'secure' && b.roleName === 'pod-reader');
        },
      },
      {
        id: 'q10-secret-mount',
        instruction:
          "**Question 10 | Monter un Secret en lecture seule**\n\nApplique `secret-mount.yaml` : le Pod `sec-app` (namespace `secure`) monte le Secret `db-creds` en volume sur `/etc/creds`.",
        hint: 'kubectl apply -f secret-mount.yaml\nkubectl -n secure describe pod sec-app',
        validate: (s) => {
          const p = pod(s, 'sec-app', 'secure');
          if (!p) return false;
          const vol = (p.volumes || []).some((v) => v.kind === 'secret' && v.source === 'db-creds');
          const mount = p.containers[0]?.volumeMounts?.some((m) => m.name === 'creds' && m.mountPath === '/etc/creds');
          return vol && !!mount;
        },
      },
      {
        id: 'q11-three-tier',
        instruction:
          "**Question 11 | Segmentation trois tiers**\n\nApplique `three-tier.yaml` (deux NetworkPolicies) dans `secure` :\n\n- `allow-web-to-api` : ingress vers `app=api` depuis `app=web`\n- `allow-api-to-db` : ingress vers `app=db` depuis `app=api`",
        hint: 'kubectl apply -f three-tier.yaml',
        validate: (s) => {
          const a = netpol(s, 'allow-web-to-api', 'secure');
          const b = netpol(s, 'allow-api-to-db', 'secure');
          const okA = !!a && a.podSelector.app === 'api' && a.ingress.some((r) => r.podSelectors.some((ps) => ps.app === 'web'));
          const okB = !!b && b.podSelector.app === 'db' && b.ingress.some((r) => r.podSelectors.some((ps) => ps.app === 'api'));
          return okA && okB;
        },
      },
      {
        id: 'q12-egress-cache',
        instruction:
          "**Question 12 | Egress restreint à un service interne**\n\nApplique `api-cache.yaml` : la NetworkPolicy `api-to-cache` autorise les pods `app=api` à joindre en egress UNIQUEMENT les pods `app=cache` sur le port `6379`.",
        hint: 'kubectl apply -f api-cache.yaml',
        validate: (s) => {
          const np = netpol(s, 'api-to-cache', 'secure');
          return !!np && np.podSelector.app === 'api' && np.policyTypes.includes('Egress') && np.egress.some((r) => r.podSelectors.some((ps) => ps.app === 'cache') && r.ports.some((p) => p.port === 6379));
        },
      },
      {
        id: 'q13-no-wildcard-rbac',
        instruction:
          "**Question 13 | Éviter les droits wildcard**\n\nUn opérateur applicatif ne doit gérer QUE les configmaps de `secure` (pas de `*`).\n\n- ServiceAccount `cfg-operator`\n- Role `cfg-manager` : `get`, `list`, `update` sur `configmaps`\n- RoleBinding `cfg-bind`",
        hint: 'kubectl -n secure create sa cfg-operator\nkubectl -n secure create role cfg-manager --verb=get,list,update --resource=configmaps\nkubectl -n secure create rolebinding cfg-bind --role=cfg-manager --serviceaccount=secure:cfg-operator',
        validate: (s) => {
          const r = role(s, 'cfg-manager', 'secure');
          const okR = !!r && hasAll(r.verbs, ['get', 'list', 'update']) && r.resources.includes('configmaps') && !r.verbs.includes('*') && !r.resources.includes('*');
          return s.serviceaccounts.some((a) => a.name === 'cfg-operator' && a.namespace === 'secure') && okR && s.rolebindings.some((b) => b.name === 'cfg-bind' && b.namespace === 'secure' && b.roleName === 'cfg-manager');
        },
      },
      {
        id: 'q14-lockdown-both',
        instruction:
          "**Question 14 | Verrouiller un pod (ingress + egress)**\n\nApplique `both-ways.yaml` : la NetworkPolicy `backend-lockdown` sur `app=backend` déclare `Ingress` ET `Egress`, et n'autorise en ingress que `app=frontend` (egress vide = tout bloqué).",
        hint: 'kubectl apply -f both-ways.yaml',
        validate: (s) => {
          const np = netpol(s, 'backend-lockdown', 'secure');
          return !!np && np.podSelector.app === 'backend' && np.policyTypes.includes('Ingress') && np.policyTypes.includes('Egress') && np.ingress.some((r) => r.podSelectors.some((ps) => ps.app === 'frontend'));
        },
      },
      {
        id: 'q15-scoped-secret-rbac',
        instruction:
          "**Question 15 | Rotation de secrets cloisonnée**\n\nDans un namespace dédié `vault`, un rotateur gère entièrement les secrets — et seulement là.\n\n- Crée le namespace `vault`\n- ServiceAccount `rotator`\n- Role `secret-admin` : `get`, `list`, `create`, `update`, `delete` sur `secrets`\n- RoleBinding `rotator-bind`",
        hint: 'kubectl create namespace vault\nkubectl -n vault create sa rotator\nkubectl -n vault create role secret-admin --verb=get,list,create,update,delete --resource=secrets\nkubectl -n vault create rolebinding rotator-bind --role=secret-admin --serviceaccount=vault:rotator',
        validate: (s) => {
          const r = role(s, 'secret-admin', 'vault');
          const okR = !!r && hasAll(r.verbs, ['get', 'list', 'create', 'update', 'delete']) && r.resources.includes('secrets');
          return s.namespaces.some((n) => n.name === 'vault') && s.serviceaccounts.some((a) => a.name === 'rotator' && a.namespace === 'vault') && okR && s.rolebindings.some((b) => b.name === 'rotator-bind' && b.namespace === 'vault' && b.roleName === 'secret-admin');
        },
      },
    ],
  },
  // ═══════════════════════════════════ CKAD (scénarios examen réalistes) ═════
  {
    id: 'ckad-bookstore-stack',
    cert: CKAD,
    title: 'Stack applicative complète dans un namespace',
    level: 'CKAD',
    summary:
      "Scénario type examen : provisionner de bout en bout un micro-service (namespace, config, secret, déploiement, exposition, montée en charge) — chaque étape est notée séparément.",
    tasks: [
      {
        id: 'ns',
        instruction: 'Crée le namespace « bookstore ».',
        hint: 'kubectl create namespace bookstore',
        validate: (s) => s.namespaces.some((n) => n.name === 'bookstore'),
      },
      {
        id: 'cm',
        instruction: 'Dans « bookstore », crée un ConfigMap « catalog-config » avec la donnée APP_ENV=production.',
        hint: 'kubectl create configmap catalog-config --from-literal=APP_ENV=production -n bookstore',
        validate: (s) => s.configmaps.some((c) => c.name === 'catalog-config' && c.namespace === 'bookstore' && c.data.APP_ENV === 'production'),
      },
      {
        id: 'secret',
        instruction: 'Dans « bookstore », crée un Secret générique « catalog-db » avec DB_PASSWORD=Sup3rS3cret.',
        hint: 'kubectl create secret generic catalog-db --from-literal=DB_PASSWORD=Sup3rS3cret -n bookstore',
        validate: (s) => s.secrets.some((sec) => sec.name === 'catalog-db' && sec.namespace === 'bookstore' && sec.data.DB_PASSWORD === 'Sup3rS3cret'),
      },
      {
        id: 'deploy',
        instruction: 'Déploie « catalog » (image « httpd:2.4 », 3 replicas) dans « bookstore ».',
        hint: 'kubectl create deployment catalog --image=httpd:2.4 --replicas=3 -n bookstore',
        validate: (s) => { const d = dep(s, 'catalog', 'bookstore'); return !!d && d.image === 'httpd:2.4' && d.replicas === 3; },
      },
      {
        id: 'expose',
        instruction: 'Expose « catalog » via un Service ClusterIP sur le port 8080 qui redirige vers le port 80 des pods (target-port 80), dans « bookstore ».',
        hint: 'kubectl expose deployment catalog --port=8080 --target-port=80 -n bookstore',
        validate: (s) => s.services.some((svc) => svc.name === 'catalog' && svc.namespace === 'bookstore' && svc.selector.app === 'catalog' && svc.port === 8080 && svc.targetPort === 80 && svc.type === 'ClusterIP'),
      },
      {
        id: 'scale',
        instruction: 'Le Black Friday approche : monte « catalog » à 5 replicas.',
        hint: 'kubectl scale deployment catalog --replicas=5 -n bookstore',
        validate: (s) => { const d = dep(s, 'catalog', 'bookstore'); return !!d && d.replicas === 5; },
      },
    ],
  },
  {
    id: 'ckad-canary',
    cert: CKAD,
    title: 'Déploiement canary avec label partagé',
    level: 'CKAD',
    summary:
      "Faire cohabiter une version stable et une version canary derrière un même Service. Trois manifestes sont fournis dans le terminal — à inspecter (cat) et appliquer.",
    files: [
      {
        path: 'stable.yaml',
        content: [
          'apiVersion: apps/v1', 'kind: Deployment', 'metadata:', '  name: shop-stable',
          '  namespace: default', 'spec:', '  replicas: 4', '  selector:', '    matchLabels:',
          '      app: shop', '      track: stable', '  template:', '    metadata:', '      labels:',
          '        app: shop', '        track: stable', '    spec:', '      containers:',
          '      - name: nginx', '        image: nginx:1.25',
        ].join('\n'),
      },
      {
        path: 'canary.yaml',
        content: [
          'apiVersion: apps/v1', 'kind: Deployment', 'metadata:', '  name: shop-canary',
          '  namespace: default', 'spec:', '  replicas: 1', '  selector:', '    matchLabels:',
          '      app: shop', '      track: canary', '  template:', '    metadata:', '      labels:',
          '        app: shop', '        track: canary', '    spec:', '      containers:',
          '      - name: nginx', '        image: nginx:1.26',
        ].join('\n'),
      },
      {
        path: 'shop-svc.yaml',
        content: [
          'apiVersion: v1', 'kind: Service', 'metadata:', '  name: shop', '  namespace: default',
          'spec:', '  selector:', '    app: shop', '  ports:', '  - port: 80', '    targetPort: 80',
        ].join('\n'),
      },
    ],
    tasks: [
      {
        id: 'stable',
        instruction: 'Applique stable.yaml : le Deployment « shop-stable » (nginx:1.25, 4 replicas).',
        hint: 'kubectl apply -f stable.yaml',
        validate: (s) => { const d = dep(s, 'shop-stable'); return !!d && d.image === 'nginx:1.25' && d.replicas === 4; },
      },
      {
        id: 'canary',
        instruction: 'Applique canary.yaml : le Deployment « shop-canary » (nginx:1.26, 1 replica) qui recevra ~20 % du trafic.',
        hint: 'kubectl apply -f canary.yaml',
        validate: (s) => { const d = dep(s, 'shop-canary'); return !!d && d.image === 'nginx:1.26' && d.replicas === 1; },
      },
      {
        id: 'svc',
        instruction: 'Applique shop-svc.yaml : un Service « shop » qui sélectionne app=shop (il route donc vers les DEUX versions).',
        hint: 'kubectl apply -f shop-svc.yaml',
        validate: (s) => s.services.some((svc) => svc.name === 'shop' && svc.selector.app === 'shop' && !svc.selector.track && svc.port === 80),
      },
    ],
  },
  {
    id: 'ckad-rolling-update',
    cert: CKAD,
    title: 'Mise à jour progressive multi-versions',
    level: 'CKAD',
    summary:
      "Enchaîner deux mises à jour d'image puis revenir à la version précédente après un incident — le cœur du domaine « Application deployment » du CKAD.",
    tasks: [
      {
        id: 'create',
        instruction: 'Crée un Deployment « web » avec l’image « nginx:1.24 » et 3 replicas.',
        hint: 'kubectl create deployment web --image=nginx:1.24 --replicas=3',
        validate: (s) => { const d = dep(s, 'web'); return !!d && d.image === 'nginx:1.24' && d.replicas === 3; },
      },
      {
        id: 'v125',
        instruction: 'Mets à jour l’image du conteneur « web » vers « nginx:1.25 ».',
        hint: 'kubectl set image deployment/web web=nginx:1.25',
        validate: (s) => { const d = dep(s, 'web'); return !!d && d.image === 'nginx:1.25'; },
      },
      {
        id: 'v126',
        instruction: 'Nouvelle version : mets à jour l’image « web » vers « nginx:1.26 ».',
        hint: 'kubectl set image deployment/web web=nginx:1.26',
        validate: (s) => { const d = dep(s, 'web'); return !!d && d.image === 'nginx:1.26' && d.history.some((h) => h.image === 'nginx:1.26'); },
      },
      {
        id: 'undo',
        instruction: 'La 1.26 casse la prod : annule le dernier déploiement (l’image doit repasser à nginx:1.25).',
        hint: 'kubectl rollout undo deployment/web',
        validate: (s) => { const d = dep(s, 'web'); return !!d && d.image === 'nginx:1.25' && d.revision >= 4; },
      },
    ],
  },
  {
    id: 'ckad-multi-container',
    cert: CKAD,
    title: 'Pod multi-conteneurs (pattern sidecar)',
    level: 'CKAD',
    summary:
      "Déployer un Pod à deux conteneurs (application + sidecar de logs) depuis un manifeste, puis l'étiqueter. Pattern incontournable du CKAD.",
    files: [
      {
        path: 'webapp.yaml',
        content: [
          'apiVersion: v1', 'kind: Pod', 'metadata:', '  name: webapp', '  namespace: default',
          '  labels:', '    app: webapp', 'spec:', '  containers:', '  - name: app',
          '    image: nginx:1.25', '  - name: log-agent', '    image: busybox:1.36',
        ].join('\n'),
      },
    ],
    tasks: [
      {
        id: 'apply',
        instruction: 'Applique webapp.yaml : un Pod « webapp » avec DEUX conteneurs — « app » (nginx:1.25) et le sidecar « log-agent » (busybox:1.36).',
        hint: 'kubectl apply -f webapp.yaml',
        validate: (s) => { const p = s.pods.find((x) => x.name === 'webapp' && x.namespace === 'default'); return !!p && p.containers.length === 2 && p.containers.some((c) => c.image === 'nginx:1.25') && p.containers.some((c) => c.image === 'busybox:1.36'); },
      },
      {
        id: 'label',
        instruction: 'Étiquette le Pod « webapp » avec le label tier=frontend.',
        hint: 'kubectl label pod webapp tier=frontend',
        validate: (s) => s.pods.some((p) => p.name === 'webapp' && p.labels.tier === 'frontend'),
      },
    ],
  },

  // ═══════════════════════════════ CKAD — série 2 (10 labs complexes) ════════
  {
    id: 'ckad-microservices', cert: CKAD, level: 'CKAD',
    title: 'Deux micro-services frontend/backend',
    summary: "Déployer un backend et un frontend dans un namespace dédié, chacun exposé par le bon type de Service.",
    tasks: [
      { id: 'ns', instruction: 'Crée le namespace « shop ».', hint: 'kubectl create namespace shop', validate: (s) => s.namespaces.some((n) => n.name === 'shop') },
      { id: 'backend', instruction: 'Déploie « backend » (httpd:2.4, 2 replicas) dans « shop ».', hint: 'kubectl create deployment backend --image=httpd:2.4 --replicas=2 -n shop', validate: (s) => { const d = dep(s, 'backend', 'shop'); return !!d && d.image === 'httpd:2.4' && d.replicas === 2; } },
      { id: 'frontend', instruction: 'Déploie « frontend » (nginx:1.25, 2 replicas) dans « shop ».', hint: 'kubectl create deployment frontend --image=nginx:1.25 --replicas=2 -n shop', validate: (s) => { const d = dep(s, 'frontend', 'shop'); return !!d && d.image === 'nginx:1.25' && d.replicas === 2; } },
      { id: 'svc-backend', instruction: 'Expose « backend » en interne : Service ClusterIP port 8080 → target 80 dans « shop ».', hint: 'kubectl expose deployment backend --port=8080 --target-port=80 -n shop', validate: (s) => s.services.some((v) => v.name === 'backend' && v.namespace === 'shop' && v.type === 'ClusterIP' && v.port === 8080 && v.targetPort === 80 && v.selector.app === 'backend') },
      { id: 'svc-frontend', instruction: 'Expose « frontend » au public : Service NodePort port 80 dans « shop ».', hint: 'kubectl expose deployment frontend --port=80 --type=NodePort -n shop', validate: (s) => s.services.some((v) => v.name === 'frontend' && v.namespace === 'shop' && v.type === 'NodePort' && v.port === 80 && v.selector.app === 'frontend') },
    ],
  },
  {
    id: 'ckad-config-manifests', cert: CKAD, level: 'CKAD',
    title: 'Configuration multi-clés par manifestes',
    summary: "Provisionner une ConfigMap à 3 clés et un Secret à 2 clés via des manifestes, puis déployer l'application.",
    files: [
      { path: 'config.yaml', content: ['apiVersion: v1', 'kind: ConfigMap', 'metadata:', '  name: app-config', '  namespace: default', 'data:', '  DATABASE_URL: postgres://db:5432/app', '  CACHE_TTL: "300"', '  FEATURE_X: enabled'].join('\n') },
      { path: 'secret.yaml', content: ['apiVersion: v1', 'kind: Secret', 'metadata:', '  name: app-secrets', '  namespace: default', 'type: Opaque', 'data:', '  API_KEY: k-abc123', '  JWT_SECRET: j-xyz789'].join('\n') },
    ],
    tasks: [
      { id: 'cm', instruction: 'Applique config.yaml : ConfigMap « app-config » (3 clés : DATABASE_URL, CACHE_TTL, FEATURE_X).', hint: 'kubectl apply -f config.yaml', validate: (s) => { const c = s.configmaps.find((x) => x.name === 'app-config'); return !!c && c.data.DATABASE_URL === 'postgres://db:5432/app' && c.data.CACHE_TTL === '300' && c.data.FEATURE_X === 'enabled'; } },
      { id: 'secret', instruction: 'Applique secret.yaml : Secret « app-secrets » (API_KEY et JWT_SECRET).', hint: 'kubectl apply -f secret.yaml', validate: (s) => { const c = s.secrets.find((x) => x.name === 'app-secrets'); return !!c && !!c.data.API_KEY && !!c.data.JWT_SECRET; } },
      { id: 'deploy', instruction: 'Déploie « app » (node:20, 2 replicas).', hint: 'kubectl create deployment app --image=node:20 --replicas=2', validate: (s) => { const d = dep(s, 'app'); return !!d && d.image === 'node:20' && d.replicas === 2; } },
    ],
  },
  {
    id: 'ckad-scale-to-zero', cert: CKAD, level: 'CKAD',
    title: 'Mise à l’échelle : arrêt puis relance',
    summary: "Mettre un worker en pause (0 replica) pour une maintenance, puis le redémarrer.",
    tasks: [
      { id: 'create', instruction: 'Crée un Deployment « worker » (busybox:1.36, 4 replicas).', hint: 'kubectl create deployment worker --image=busybox:1.36 --replicas=4', validate: (s) => { const d = dep(s, 'worker'); return !!d && d.replicas === 4; } },
      { id: 'zero', instruction: 'Maintenance : mets « worker » à 0 replica.', hint: 'kubectl scale deployment worker --replicas=0', validate: (s) => { const d = dep(s, 'worker'); return !!d && d.replicas === 0; } },
      { id: 'restart', instruction: 'Fin de maintenance : redémarre « worker » avec 3 replicas.', hint: 'kubectl scale deployment worker --replicas=3', validate: (s) => { const d = dep(s, 'worker'); return !!d && d.replicas === 3; } },
    ],
  },
  {
    id: 'ckad-blue-green', cert: CKAD, level: 'CKAD',
    title: 'Déploiement blue/green avec bascule',
    summary: "Faire coexister deux versions (blue/green) et basculer le Service de l'une à l'autre. Manifestes fournis.",
    files: [
      { path: 'blue.yaml', content: ['apiVersion: apps/v1', 'kind: Deployment', 'metadata:', '  name: app-blue', '  namespace: default', 'spec:', '  replicas: 3', '  selector:', '    matchLabels:', '      app: myapp', '      color: blue', '  template:', '    metadata:', '      labels:', '        app: myapp', '        color: blue', '    spec:', '      containers:', '      - name: nginx', '        image: nginx:1.25'].join('\n') },
      { path: 'green.yaml', content: ['apiVersion: apps/v1', 'kind: Deployment', 'metadata:', '  name: app-green', '  namespace: default', 'spec:', '  replicas: 3', '  selector:', '    matchLabels:', '      app: myapp', '      color: green', '  template:', '    metadata:', '      labels:', '        app: myapp', '        color: green', '    spec:', '      containers:', '      - name: nginx', '        image: nginx:1.26'].join('\n') },
      { path: 'svc-green.yaml', content: ['apiVersion: v1', 'kind: Service', 'metadata:', '  name: myapp', '  namespace: default', 'spec:', '  selector:', '    app: myapp', '    color: green', '  ports:', '  - port: 80', '    targetPort: 80'].join('\n') },
    ],
    tasks: [
      { id: 'blue', instruction: 'Applique blue.yaml (Deployment « app-blue », nginx:1.25).', hint: 'kubectl apply -f blue.yaml', validate: (s) => { const d = dep(s, 'app-blue'); return !!d && d.image === 'nginx:1.25'; } },
      { id: 'green', instruction: 'Applique green.yaml (Deployment « app-green », nginx:1.26).', hint: 'kubectl apply -f green.yaml', validate: (s) => { const d = dep(s, 'app-green'); return !!d && d.image === 'nginx:1.26'; } },
      { id: 'switch', instruction: 'Bascule le Service « myapp » vers la version green : applique svc-green.yaml (sélecteur color=green).', hint: 'kubectl apply -f svc-green.yaml', validate: (s) => s.services.some((v) => v.name === 'myapp' && v.selector.app === 'myapp' && v.selector.color === 'green') },
    ],
  },
  {
    id: 'ckad-adapter-pod', cert: CKAD, level: 'CKAD',
    title: 'Pod à 3 conteneurs (app + proxy + cache)',
    summary: "Assembler un Pod applicatif à plusieurs conteneurs depuis un manifeste, puis l'étiqueter pour un Service.",
    files: [
      { path: 'edge.yaml', content: ['apiVersion: v1', 'kind: Pod', 'metadata:', '  name: edge', '  namespace: default', '  labels:', '    app: edge', 'spec:', '  containers:', '  - name: app', '    image: nginx:1.25', '  - name: proxy', '    image: haproxy:2.9', '  - name: cache', '    image: redis:7'].join('\n') },
    ],
    tasks: [
      { id: 'apply', instruction: 'Applique edge.yaml : un Pod « edge » à 3 conteneurs (app nginx, proxy haproxy, cache redis).', hint: 'kubectl apply -f edge.yaml', validate: (s) => { const p = s.pods.find((x) => x.name === 'edge'); return !!p && p.containers.length === 3 && p.containers.some((c) => c.image === 'haproxy:2.9') && p.containers.some((c) => c.image === 'redis:7'); } },
      { id: 'label', instruction: 'Étiquette le Pod « edge » avec tier=edge.', hint: 'kubectl label pod edge tier=edge', validate: (s) => s.pods.some((p) => p.name === 'edge' && p.labels.tier === 'edge') },
    ],
  },
  {
    id: 'ckad-targetport', cert: CKAD, level: 'CKAD',
    title: 'Service avec port et targetPort distincts',
    summary: "Exposer une application dont le conteneur écoute sur 8080 via un Service standard sur le port 80.",
    tasks: [
      { id: 'deploy', instruction: 'Crée un Deployment « api » (image « myorg/api:1.0 », 3 replicas).', hint: 'kubectl create deployment api --image=myorg/api:1.0 --replicas=3', validate: (s) => { const d = dep(s, 'api'); return !!d && d.image === 'myorg/api:1.0' && d.replicas === 3; } },
      { id: 'expose', instruction: 'Expose « api » : Service ClusterIP sur le port 80 qui redirige vers le port 8080 des pods.', hint: 'kubectl expose deployment api --port=80 --target-port=8080', validate: (s) => s.services.some((v) => v.name === 'api' && v.port === 80 && v.targetPort === 8080 && v.selector.app === 'api') },
    ],
  },
  {
    id: 'ckad-rollout-history', cert: CKAD, level: 'CKAD',
    title: 'Historique de déploiement sur 3 versions',
    summary: "Enchaîner trois versions d'image et vérifier que l'historique du Deployment les conserve toutes.",
    tasks: [
      { id: 'v23', instruction: 'Crée « site » avec l’image « nginx:1.23 ».', hint: 'kubectl create deployment site --image=nginx:1.23', validate: (s) => { const d = dep(s, 'site'); return !!d && d.image === 'nginx:1.23'; } },
      { id: 'v24', instruction: 'Mets à jour l’image vers « nginx:1.24 ».', hint: 'kubectl set image deployment/site nginx=nginx:1.24', validate: (s) => { const d = dep(s, 'site'); return !!d && d.image === 'nginx:1.24'; } },
      { id: 'v25', instruction: 'Mets à jour l’image vers « nginx:1.25 ». L’historique doit contenir les 3 versions.', hint: 'kubectl set image deployment/site nginx=nginx:1.25', validate: (s) => { const d = dep(s, 'site'); return !!d && d.image === 'nginx:1.25' && d.revision >= 3 && ['nginx:1.23', 'nginx:1.24', 'nginx:1.25'].every((img) => d.history.some((h) => h.image === img)); } },
    ],
  },
  {
    id: 'ckad-debug-run', cert: CKAD, level: 'CKAD',
    title: 'Pod de debug et déploiement exposé',
    summary: "Lancer rapidement un pod de diagnostic, puis déployer et exposer un service web.",
    tasks: [
      { id: 'debug', instruction: 'Lance un Pod « debug » (busybox:1.36) pour diagnostiquer le cluster.', hint: 'kubectl run debug --image=busybox:1.36', validate: (s) => s.pods.some((p) => p.name === 'debug' && p.containers[0]?.image === 'busybox:1.36') },
      { id: 'deploy', instruction: 'Déploie « web » (nginx:1.25, 2 replicas).', hint: 'kubectl create deployment web --image=nginx:1.25 --replicas=2', validate: (s) => { const d = dep(s, 'web'); return !!d && d.replicas === 2; } },
      { id: 'expose', instruction: 'Expose « web » via un Service ClusterIP sur le port 80.', hint: 'kubectl expose deployment web --port=80', validate: (s) => s.services.some((v) => v.name === 'web' && v.port === 80 && v.selector.app === 'web') },
    ],
  },
  {
    id: 'ckad-ns-app-stack', cert: CKAD, level: 'CKAD',
    title: 'Application isolée avec config et exposition',
    summary: "Provisionner une application complète (namespace, configmap, déploiement, service) pour l'environnement v2.",
    tasks: [
      { id: 'ns', instruction: 'Crée le namespace « v2 ».', hint: 'kubectl create namespace v2', validate: (s) => s.namespaces.some((n) => n.name === 'v2') },
      { id: 'cm', instruction: 'Crée un ConfigMap « api-cfg » (LOG_LEVEL=info) dans « v2 ».', hint: 'kubectl create configmap api-cfg --from-literal=LOG_LEVEL=info -n v2', validate: (s) => s.configmaps.some((c) => c.name === 'api-cfg' && c.namespace === 'v2' && c.data.LOG_LEVEL === 'info') },
      { id: 'deploy', instruction: 'Déploie « api » (httpd:2.4, 3 replicas) dans « v2 ».', hint: 'kubectl create deployment api --image=httpd:2.4 --replicas=3 -n v2', validate: (s) => { const d = dep(s, 'api', 'v2'); return !!d && d.replicas === 3; } },
      { id: 'expose', instruction: 'Expose « api » en ClusterIP sur le port 80 dans « v2 ».', hint: 'kubectl expose deployment api --port=80 -n v2', validate: (s) => s.services.some((v) => v.name === 'api' && v.namespace === 'v2' && v.port === 80) },
    ],
  },
  {
    id: 'ckad-canary-promote', cert: CKAD, level: 'CKAD',
    title: 'Promotion d’une version canary',
    summary: "Introduire une version canary, la monter en charge progressivement puis retirer l'ancienne version.",
    tasks: [
      { id: 'v1', instruction: 'Crée « app-v1 » (nginx:1.25, 4 replicas).', hint: 'kubectl create deployment app-v1 --image=nginx:1.25 --replicas=4', validate: (s) => { const d = dep(s, 'app-v1'); return !!d && d.replicas === 4; } },
      { id: 'v2', instruction: 'Introduis la canary « app-v2 » (nginx:1.26, 1 replica).', hint: 'kubectl create deployment app-v2 --image=nginx:1.26 --replicas=1', validate: (s) => { const d = dep(s, 'app-v2'); return !!d && d.image === 'nginx:1.26' && d.replicas === 1; } },
      { id: 'promote', instruction: 'La canary est saine : monte « app-v2 » à 4 replicas.', hint: 'kubectl scale deployment app-v2 --replicas=4', validate: (s) => { const d = dep(s, 'app-v2'); return !!d && d.replicas === 4; } },
      { id: 'retire', instruction: 'Retire l’ancienne version : mets « app-v1 » à 0 replica.', hint: 'kubectl scale deployment app-v1 --replicas=0', validate: (s) => { const d = dep(s, 'app-v1'); return !!d && d.replicas === 0; } },
    ],
  },

];

// Injecte la problématique (2-3 paragraphes) de chaque lab depuis labScenarios.ts,
// et REMPLACE la consigne des tâches par la version « format examen » (puces +
// chips de code) quand elle existe dans labInstructions.ts.
for (const lab of LABS) {
  lab.scenario = LAB_SCENARIOS[lab.id] ?? lab.scenario ?? lab.summary;
  for (const task of lab.tasks) {
    const fmt = LAB_INSTRUCTIONS[`${lab.id}/${task.id}`];
    if (fmt) task.instruction = fmt;
  }
}

export function getLab(id: string): Lab | undefined {
  return LABS.find((l) => l.id === id);
}

export function getLabsForCert(certSlug: string): Lab[] {
  return LABS.filter((l) => l.cert === certSlug);
}
